/**
 * Centralized Test ID Constants
 *
 * Provides consistent test IDs for QA and testing purposes.
 * Follows pattern: {component}-{element}-{identifier?}
 *
 * Usage:
 * ```tsx
 * <Button data-testid={testIds.documentViewer.saveButton}>
 *   Save
 * </Button>
 * ```
 */

export function leftSidebarWorkspacePeopleRow(workspaceId: string): string {
  return `left-sidebar-workspace-${workspaceId}-people-row`
}

export const testIds = {
  appLayout: {
    container: 'app-layout',
  },
  auth: {
    loginForm: 'auth-login-form',
    resetPasswordForm: 'auth-reset-password-form',
    forgotPasswordForm: 'auth-forgot-password-form',
    expiredInviteForm: 'auth-expired-invite-form',
    expiredInviteEmail: 'auth-expired-invite-email',
    expiredInviteSubmit: 'auth-expired-invite-submit',
  },
  documentViewer: {
    container: 'document-viewer',
    /** Reading column under the real toolbar. In-document review mounts here. */
    readingPane: 'document-viewer-reading-pane',
    toolbar: 'document-viewer-toolbar',
    backButton: 'document-viewer-back-button',
    forwardButton: 'document-viewer-forward-button',
    breadcrumbs: 'document-viewer-breadcrumbs',
    breadcrumbsPlaceholder: 'document-viewer-breadcrumbs-placeholder',
    title: 'document-viewer-title',
    titleCompleted: 'document-viewer-title-completed',
    titleInput: 'document-viewer-title-input',
    saveButton: 'document-viewer-save-button',
    generateAudio: 'document-viewer-generate-audio',
    undoButton: 'document-viewer-undo-button',
    /** History segment inside Document Info dock (was a Clock toolbar icon). */
    versionHistoryToggle: 'document-viewer-version-history-toggle',
    /** Overflow menu: Show / Hide outline (heading nav) */
    outlineMenuItem: 'document-viewer-menu-outline',
    /** @deprecated moved into the ⋮ menu (see pushToDriveMenuItem) - Phase 2 toolbar-density cleanup */
    pushToDriveButton: 'document-viewer-push-to-drive-button',
    /** @deprecated moved into the ⋮ menu (see pullFromDriveMenuItem) - Phase 2 toolbar-density cleanup */
    pullFromDriveButton: 'document-viewer-pull-from-drive-button',
    gdocSyncBadge: 'document-viewer-gdoc-sync-badge',
    gdocCommentCount: 'document-viewer-gdoc-comment-count',
    gdocLastSync: 'document-viewer-gdoc-last-sync',
    infoButton: 'document-viewer-info-button',
    menuButton: 'document-viewer-menu-button',
    /** State-aware Open in Cursor / Configure git menu (todo-linked documents) */
    cursorWorkspaceMenu: 'document-viewer-cursor-workspace-menu',
    showFileLocationMenuItem: 'document-viewer-menu-show-file-location',
    revealInSidebarMenuItem: 'document-viewer-menu-reveal-in-sidebar',
    copyUrlMenuItem: 'document-viewer-menu-copy-url',
    /** Copies `flownotes:{filePath}` for the Cursor extension - moved from a persistent icon into the ⋮ menu (Phase 2 toolbar-density cleanup) */
    copyPathMenuItem: 'document-viewer-menu-copy-path',
    /** Refresh document/roadmap - moved from a persistent icon into the ⋮ menu (Phase 2 toolbar-density cleanup) */
    refreshMenuItem: 'document-viewer-menu-refresh',
    indexSearchMenuItem: 'document-viewer-menu-index-search',
    activityListeningMenuItem: 'document-viewer-menu-activity-listening',
    activityOpenBasecampMenuItem: 'document-viewer-menu-activity-open-basecamp',
    /** Force pull from Google Drive (advanced overwrite) */
    pullFromDriveMenuItem: 'document-viewer-menu-pull-from-drive',
    /** Force push to Google Drive (advanced overwrite) */
    pushToDriveMenuItem: 'document-viewer-menu-push-to-drive',
    /** Primary Sync action for linked Google Docs (header badge) */
    gdocSyncButton: 'document-viewer-gdoc-sync-button',
    /** Keep synced toggle for linked Google Docs (header badge) */
    gdocKeepSyncedToggle: 'document-viewer-gdoc-keep-synced-toggle',
    /** Overflow menu: Google Drive submenu trigger */
    gdocMenuSubTrigger: 'document-viewer-menu-google-drive',
    /** Overflow menu: open canonical Google Docs URL */
    gdocOpenInDriveMenuItem: 'document-viewer-menu-open-in-google-drive',
    /** Overflow menu: Sync with Drive (nested under Google Drive) */
    gdocSyncMenuItem: 'document-viewer-menu-gdoc-sync',
    /** Overflow menu: Keep synced toggle (nested under Google Drive) */
    gdocKeepSyncedMenuItem: 'document-viewer-menu-gdoc-keep-synced',
    /** Header badge: open in Google Drive */
    gdocOpenInDriveButton: 'document-viewer-gdoc-open-in-drive',
    /** Overflow menu: Publish unlinked Maater doc as a new Google Doc */
    gdocPublishMenuItem: 'document-viewer-menu-publish-to-drive',
    /** Overflow menu: Unlink Maater doc from Google Doc (doc stays in Drive) */
    gdocDisconnectMenuItem: 'document-viewer-menu-disconnect-from-drive',
    /** Drive sync activity in document bottom status bar (not a top banner) */
    gdocSyncActivityStrip: 'document-viewer-gdoc-sync-activity-strip',
    content: 'document-viewer-content',
    editor: 'document-viewer-editor',
    /** Drop .md/.txt onto an open document / tab bar to import as a sibling in the same folder */
    textImportDropZone: 'document-viewer-text-import-drop-zone',
    /** Ghost tab preview shown while dragging local text files over the document chrome */
    textImportGhostTab: 'document-viewer-text-import-ghost-tab',
    /** Full-panel overlay path message during local text import drop */
    textImportOverlay: 'document-viewer-text-import-overlay',
    /** Suspense fallback while lazy-loaded panel chunks load */
    lazyFallback: 'document-viewer-lazy-fallback',
    /** Cold document deep-link mid-load shell (replaces Home flash) */
    deepLinkLoadingShell: 'document-deep-link-loading-shell',
    preview: 'document-viewer-preview',
    sidebar: 'document-viewer-sidebar',
    tab: (id: string) => `document-tab-${id}`,
    tabList: 'document-tab-list',
    tabClose: (id: string) => `document-tab-close-${id}`,
    tabPin: (id: string) => `document-tab-pin-${id}`,
    gdocTabsNav: 'document-viewer-gdoc-tabs-nav',
    gdocTab: (id: string) => `document-viewer-gdoc-tab-${id}`,
    softBookmarkHereMenuItem: 'document-viewer-menu-soft-bookmark-here',
    softBookmarkJumpMenuItem: 'document-viewer-menu-soft-bookmark-jump',
    softBookmarkClearMenuItem: 'document-viewer-menu-soft-bookmark-clear',
    softBookmarksMenuLabel: 'document-viewer-menu-soft-bookmarks-label',
    softBookmarksEnabledSwitch: 'document-viewer-menu-soft-bookmarks-enabled',
    /** Open doc belongs to a workspace toggled off in the sidebar switcher */
    inactiveWorkspaceBanner: 'document-viewer-inactive-workspace-banner',
    inactiveWorkspaceEnableButton: 'document-viewer-inactive-workspace-enable',
    /** Soft banner when viewing a document under archive/ with status archived */
    archivedBanner: 'document-viewer-archived-banner',
    archivedBannerUnarchiveButton: 'document-viewer-archived-banner-unarchive',
    archiveMenuItem: 'document-viewer-menu-archive',
    unarchiveMenuItem: 'document-viewer-menu-unarchive',
  },

  documentInfoDock: {
    root: 'document-info-dock',
    modeTabs: 'document-info-dock-mode-tabs',
    detailsTab: 'document-info-dock-details-tab',
    commentsTab: 'document-info-dock-comments-tab',
  },
  documentInfoPane: {
    /** Compact UUID copy chip (icon + width-truncated value; whole control copies) */
    uuid: 'document-info-uuid',
    /** @deprecated Separate copy affordance removed; use `uuid` on the chip. */
    uuidCopyButton: 'document-info-uuid-copy-button',
    /** Compact PATH copy chip (narrow = basename; whole control copies full path) */
    path: 'document-info-path',
    messageStalenessBanner: 'document-info-message-staleness-banner',
    frontmatterToggle: 'document-info-frontmatter-toggle',
    frontmatterSection: 'document-info-frontmatter-section',
    /** METADATA deep-link into History dock mode */
    viewHistoryLink: 'document-info-view-history',
  },
  chatPane: {
    container: 'chat-pane',
    messages: 'chat-pane-messages',
    welcome: 'chat-pane-welcome',
    input: 'chat-pane-input',
    sendButton: 'chat-pane-send-button',
    /** Keyword skill offer under the draft ("Use Pilot Feedback?") */
    skillOffer: 'chat-pane-skill-offer',
    references: 'chat-pane-references',
    reference: (id: string) => `chat-reference-${id}`,
    referenceRemove: (id: string) => `chat-reference-remove-${id}`,
    message: (index: number) => `chat-message-${index}`,
    messageUser: (index: number) => `chat-message-user-${index}`,
    messageAssistant: (index: number) => `chat-message-assistant-${index}`,
    messageToolCall: (index: number) => `chat-message-tool-call-${index}`,
    /** Inline row for successful getDocument (context read, not a user-facing “open” action). */
    messageToolQuietGetDocument: 'chat-message-tool-quiet-get-document',
    /** Soft recoverable tool miss (wrong slug, no captions) - muted, not alarm red */
    messageToolSoftFailure: 'chat-message-tool-soft-failure',
    providerSelector: 'chat-pane-provider-selector',
    modelSelector: 'chat-pane-model-selector',
    /** Cursor-style context window fullness ring beside the model selector */
    contextMeter: 'chat-pane-context-meter',
    modeSelector: 'chat-mode-selector',
    /** Plan mode: switch to Agent and implement / create drafted files */
    planBuildButton: 'chat-plan-build-button',
    /** Plan Build harness row (not a user bubble) */
    planBuildHarness: 'chat-plan-build-harness',
    historyButton: 'chat-pane-history-button',
    feedbackButton: 'chat-pane-feedback-button',
    collapseButton: 'chat-pane-collapse-button',
    /** Edit-and-rewind composer (user bubble) */
    editInput: 'chat-pane-edit-input',
    /** Expand/collapse control for long user prompts */
    userMessageExpand: 'chat-pane-user-message-expand',
    /** Compact Update Work Spec seed (hides machine prompt / preflight dump) */
    updateWorkSpecSeed: 'chat-pane-update-work-spec-seed',
    /** Floating collapse chip while a long prompt is expanded */
    userMessageCollapseFloat: 'chat-pane-user-message-collapse-float',
    postedImage: 'chat-pane-posted-image',
    postedImageLightbox: 'chat-pane-posted-image-lightbox',
    postedImageLightboxCaption: 'chat-pane-posted-image-lightbox-caption',
    postedImageLightboxPrev: 'chat-pane-posted-image-lightbox-prev',
    postedImageLightboxNext: 'chat-pane-posted-image-lightbox-next',
    postedImageMenu: 'chat-pane-posted-image-menu',
    postedImageMenuOpen: 'chat-pane-posted-image-menu-open',
    postedImageMenuEdit: 'chat-pane-posted-image-menu-edit',
    /** Jump to latest when scrolled up in a long thread */
    jumpToBottom: 'chat-pane-jump-to-bottom',
    /** Dry-run Basecamp create confirm card */
    basecampCreateTodoDraft: 'chat-pane-basecamp-create-todo-draft',
    basecampDraftCollapsed: 'chat-pane-basecamp-draft-collapsed',
    basecampCreateTodoConfirm: 'chat-pane-basecamp-create-todo-confirm',
    basecampCreateTodoCancel: 'chat-pane-basecamp-create-todo-cancel',
    basecampCreateTodoLink: 'chat-pane-basecamp-create-todo-link',
    /** Info control: how save-and-rewind works */
    editRewindInfo: 'chat-pane-edit-rewind-info',
    /** Queued prompts strip above main composer */
    queueStrip: 'chat-pane-queue-strip',
    queueItem: 'chat-pane-queue-item',
    queueItemImages: 'chat-pane-queue-item-images',
    queueItemSendNow: 'chat-pane-queue-item-send-now',
    queueItemEdit: (id: string) => `chat-pane-queue-item-edit-${id}`,
    queueEditBanner: 'chat-pane-queue-edit-banner',
    queueEditCancel: 'chat-pane-queue-edit-cancel',
    /** Border-t shell around main composer (padding / gutter) */
    composerFooter: 'chat-pane-composer-footer',
    composerContextRow: 'chat-pane-composer-context-row',
    composerScope: 'chat-pane-composer-scope',
    /** Dedicated row under the widget toolbar for auto-tagged doc + scope */
    composerExtrasRow: 'chat-pane-composer-extras-row',
    sessionFiles: 'chat-pane-session-files',
    sessionFilesFile: 'chat-pane-session-files-file',
    sessionFilesBin: 'chat-pane-session-files-bin',
    sessionFilesToggle: 'chat-pane-session-files-toggle',
    flowAgentOrigin: 'chat-pane-flow-agent-origin',
    flowAgentViewInbox: 'chat-pane-flow-agent-view-inbox',
    flowAgentViewFlow: 'chat-pane-flow-agent-view-flow',
    /** Staged image paste budget meter above composer thumbnails */
    imageBudgetMeter: 'chat-image-budget-meter',
    /** Legacy ingestion-mode composer (auto-growing textarea + send) */
    ingestionInputContainer: 'chat-pane-ingestion-input-container',
    ingestionInput: 'chat-pane-ingestion-input',
    ingestionSendButton: 'chat-pane-ingestion-send',
    /** FileSummary card rendered after ingestion-success messages */
    fileSummaryCard: 'chat-file-summary-card',
    fileSummaryRow: (index: number) => `chat-file-summary-row-${index}`,
    /** Grouped tool-call card (BatchToolMessages) */
    toolGroupCard: 'chat-tool-group-card',
    toolGroupHeader: 'chat-tool-group-header',
    toolGroupRows: 'chat-tool-group-rows',
    /** Unified Thinking timeline (live tool chrome) */
    thinkingTimeline: 'chat-thinking-timeline',
    thinkingTimelineHeader: 'chat-thinking-timeline-header',
    thinkingTimelineScroll: 'chat-thinking-timeline-scroll',
    thinkingTimelineHardError: 'chat-thinking-timeline-hard-error',
    thinkingTimelineStep: (stepId: string) => `chat-thinking-timeline-step-${stepId}`,
    thinkingTimelineStepDetails: (stepId: string) =>
      `chat-thinking-timeline-step-details-${stepId}`,
    thinkingTimelineDocRow: (docId: string) => `chat-thinking-timeline-doc-${docId}`,
    /** Agentic cap banner (Continue working / Continuing...) */
    capBanner: 'chat-pane-cap-banner',
    capBannerContinuing: 'chat-pane-cap-banner-continuing',
    continueWorking: 'chat-pane-continue-working',
    /** Spinner under latest assistant while Stop is showing and tokens/tools are quiet */
    streamWorking: 'chat-pane-stream-working',
    streamWaitStatus: 'chat-pane-stream-wait-status',
    /** Collapsed historical fake `[createDocument]` narration */
    fakeToolCollapse: 'chat-fake-tool-collapse',
    /** Ingestion run card (collapsible group of progress messages) */
    ingestionRunCard: 'chat-ingestion-run-card',
    ingestionRunHeader: 'chat-ingestion-run-header',
    ingestionRunRows: 'chat-ingestion-run-rows',
    /** Thinking mode: soul / persona load (distinct UI from generic search rows) */
    thinkingPersonaRow: 'chat-thinking-persona-row',
    /** Portaled `/` skills menu */
    slashCommandMenu: 'chat-slash-command-menu',
    slashSkillOption: (skillId: string) => `chat-slash-skill-${skillId}`,
    /** Sticky banner when unaccepted proposed edits are pending */
    pendingEditsBanner: 'chat-pending-edits-banner',
    pendingEditsReview: 'chat-pending-edits-review',
    /** Cursor-style modified files bar above composer */
    modifiedFilesPanel: 'chat-modified-files-panel',
    modifiedFilesUndo: 'chat-modified-files-undo',
    modifiedFilesKeep: 'chat-modified-files-keep',
    modifiedFilesReview: 'chat-modified-files-review',
    actionChoicePrimary: (index: number) => `chat-action-choice-primary-${index}`,
    actionChoiceSecondary: (index: number) => `chat-action-choice-secondary-${index}`,
    quizCreatedCard: 'chat-quiz-created-card',
    quizCreatedOpen: 'chat-quiz-created-open',
  },
  multipleChoiceForm: {
    root: 'multiple-choice-form',
    question: 'multiple-choice-form-question',
    option: (id: string) => `multiple-choice-form-option-${id}`,
    correctAnswer: 'multiple-choice-form-correct-answer',
    explanation: 'multiple-choice-form-explanation',
  },
  choiceBlock: {
    root: 'choice-block',
    progress: 'choice-block-progress',
    next: 'choice-block-next',
    submit: 'choice-block-submit',
    /** Free-text field when plan-ask Other is selected */
    otherInput: 'choice-block-other-input',
    /** Compact badge after plan-ask Continue */
    answered: 'choice-block-answered',
  },
  flashCardWidget: {
    root: 'flash-card-widget',
    summary: 'flash-card-widget-summary',
    pauseBar: 'flash-card-pause-bar',
    pausedBanner: 'flash-card-paused-banner',
    pause: 'flash-card-pause',
    resume: 'flash-card-resume',
    resumeInline: 'flash-card-resume-inline',
    dismiss: 'flash-card-dismiss',
    dismissCard: 'flash-card-dismiss-card',
    dismissed: 'flash-card-dismissed',
  },
  chatFirstHome: {
    root: 'chat-first-home',
    composerSlot: 'chat-first-home-composer',
  },
  chatNewConversationWelcome: {
    root: 'chat-first-home',
    suggestion: (id: string) => `chat-welcome-suggestion-${id}`,
  },
  commandPalette: {
    container: 'command-palette',
    input: 'command-palette-input',
    searchError: 'command-palette-search-error',
    results: 'command-palette-results',
    result: (index: number) => `command-result-${index}`,
    resultDocument: (index: number) => `command-result-document-${index}`,
    resultAction: (index: number) => `command-result-action-${index}`,
    resultUrl: (index: number) => `command-result-url-${index}`,
    prefixChipBar: 'command-palette-prefix-chip-bar',
    scopeRow: 'command-palette-scope-row',
    scopeTrigger: 'command-palette-scope-trigger',
    chatPromptRow: 'command-palette-chat-prompt-row',
    driveImportToFolder: 'command-palette-drive-import-to-folder',
    driveChooseFolder: 'command-palette-drive-choose-folder',
  },
  documentPreviewFind: {
    bar: 'document-preview-find-bar',
    input: 'document-preview-find-input',
    counter: 'document-preview-find-counter',
    prev: 'document-preview-find-prev',
    next: 'document-preview-find-next',
    close: 'document-preview-find-close',
  },
  tiptapFind: {
    bar: 'tiptap-find-bar',
    input: 'tiptap-find-input',
    counter: 'tiptap-find-counter',
    prev: 'tiptap-find-prev',
    next: 'tiptap-find-next',
    close: 'tiptap-find-close',
  },
  documentList: {
    container: 'document-list',
    toolbar: 'document-list-toolbar',
    title: 'document-list-title',
    menuButton: 'document-list-menu-button',
    reloadButton: 'document-list-reload-button',
    previewToggle: 'document-list-preview-toggle',
    folderGrid: 'document-list-folder-grid',
    fileGrid: 'document-list-file-grid',
    panelSearch: 'document-list-panel-search',
    filters: 'document-list-filters',
    typeFilter: 'document-list-type-filter',
    authorFilter: 'document-list-author-filter',
    sort: 'document-list-sort',
    viewMode: 'document-list-view-mode',
    /** Sticky list column header bar */
    listHeader: 'document-list-header',
    /** Clickable column sort control: name | type | modified | lines | size | author */
    sortColumn: (column: string) => `document-list-sort-${column}`,
    /** Center folder-view drop zone for local .md/.txt import */
    folderDropZone: 'document-list-folder-drop-zone',
    item: (id: string) => `document-item-${id}`,
    itemCard: (id: string) => `document-item-card-${id}`,
    itemGrid: (id: string) => `document-item-grid-${id}`,
    loadMore: 'document-list-load-more',
    backgroundMenu: 'document-list-background-menu',
    newDocument: 'document-list-new-document',
    newFolder: 'document-list-new-folder',
    renameFolder: 'document-list-rename-folder',
    moveFolder: 'document-list-move-folder',
    deleteFolder: 'document-list-delete-folder',
    previewMarkdown: 'document-list-preview-markdown',
    previewPane: 'document-list-preview-pane',
    previewHeader: 'document-list-preview-header',
    previewFolderList: 'document-list-preview-folder-list',
    previewFolderTitle: 'document-list-preview-folder-title',
    previewLocation: 'document-list-preview-location',
    previewFolderMeta: 'document-list-preview-folder-meta',
    previewActions: 'document-list-preview-actions',
    statusBar: 'document-list-status-bar',
    statusBarHelp: 'document-list-status-bar-help',
    statusBarMeta: 'document-list-status-bar-meta',
    headerSlot: 'document-list-header-slot',
    searchSlot: 'document-list-search-slot',
    footerSlot: 'document-list-footer-slot',
    urlCollision: 'document-list-url-collision',
    /** Basecamp todo strip shown when browsing a `todos/{todoId}-…` folder */
    todoStripViewInFlownotes: 'document-list-todo-strip-view-in-flownotes',
    todoStripOpenBasecamp: 'document-list-todo-strip-open-basecamp',
    todoStripWorkSpec: 'document-list-todo-strip-work-spec',
    todoStripOpenInCursor: 'document-list-todo-strip-open-in-cursor',
  },
  /** Cursor workspace actions menu (folder toolbar todo strip) - state-aware replacement for the bare "Open in Cursor" button. */
  cursorWorkspaceMenu: {
    trigger: 'cursor-workspace-menu-trigger',
    openInCursor: 'cursor-workspace-menu-open-in-cursor',
    openTaskContextOnly: 'cursor-workspace-menu-open-task-context-only',
    materializeLocally: 'cursor-workspace-menu-materialize-locally',
    copyWorkspaceUri: 'cursor-workspace-menu-copy-workspace-uri',
    configureGitRepository: 'cursor-workspace-menu-configure-git-repository',
    linkProject: 'cursor-workspace-menu-link-project',
    linkDialog: 'cursor-workspace-menu-link-dialog',
  },
  markdownEditor: {
    readOnlyPreview: 'markdown-editor-readonly-preview',
    wysiwygPreview: 'markdown-editor-wysiwyg-preview',
    wysiwygEdit: 'markdown-editor-wysiwyg-edit',
  },
  tiptapFormattingToolbar: {
    root: 'tiptap-formatting-toolbar',
    bold: 'tiptap-formatting-toolbar-bold',
    italic: 'tiptap-formatting-toolbar-italic',
    strike: 'tiptap-formatting-toolbar-strike',
    code: 'tiptap-formatting-toolbar-code',
    link: 'tiptap-formatting-toolbar-link',
    /** @deprecated use heading(level) */
    heading2: 'tiptap-formatting-toolbar-heading-2',
    heading: (level: number) => `tiptap-formatting-toolbar-heading-${level}`,
    bulletList: 'tiptap-formatting-toolbar-bullet-list',
    orderedList: 'tiptap-formatting-toolbar-ordered-list',
    taskList: 'tiptap-formatting-toolbar-task-list',
  },
  documentContentStatusBar: {
    root: 'document-content-status-bar',
    editToggle: 'document-content-status-bar-edit-toggle',
    viewModeTabs: 'document-content-status-bar-view-mode-tabs',
    pageWidthToggle: 'document-content-status-bar-page-width-toggle',
    diffViewModeTabs: 'document-content-status-bar-diff-view-mode-tabs',
    infoButton: 'document-content-status-bar-info-button',
  },
  /** HTML document preview chrome. Lives in the renderer, not the HTML file. */
  documentContentHtmlStatusBar: {
    root: 'document-content-html-status-bar',
    stats: 'document-content-html-status-bar-stats',
  },
  csvDocument: {
    root: 'csv-document-viewer',
    tableMode: 'csv-document-table-mode',
    sourceMode: 'csv-document-source-mode',
    table: 'csv-document-table',
    source: 'csv-document-source',
    parseError: 'csv-document-parse-error',
  },
  documentTabs: {
    pinIcon: 'document-tabs-pin-icon',
    newDocument: 'document-tabs-new-document',
    newDocumentPrimary: 'document-tabs-new-document-primary',
    newDocumentMenu: 'document-tabs-new-document-menu',
    newDocumentInFolder: 'document-tabs-new-document-in-folder',
    newDocumentImport: 'document-tabs-new-document-import',
    newDocumentNewFolder: 'document-tabs-new-document-new-folder',
    newDocumentChooseFolder: 'document-tabs-new-document-choose-folder',
    groupHeader: (workspaceId: string) => `document-tabs-group-header-${workspaceId}`,
    groupToggle: (workspaceId: string) => `document-tabs-group-toggle-${workspaceId}`,
  },
  skills: {
    documentBadge: 'skill-document-badge',
    platformPreview: 'skill-platform-preview',
  },
  documentVersionHistory: {
    panel: 'document-version-history-panel',
    row: (editId: string) => `document-version-history-row-${editId}`,
    milestoneRow: (editId: string) => `document-version-history-milestone-${editId}`,
    timeCluster: (clusterId: string) => `document-version-history-time-${clusterId}`,
    diffDialog: 'document-version-history-diff-dialog',
    /** Knowledge Hub "append changelog note" affordance, ported from the standalone review-changelog panel. */
    appendNoteInput: 'document-version-history-append-note-input',
    appendNoteButton: 'document-version-history-append-note-button',
  },
  knowledgeHub: {
    statusStrip: 'knowledge-hub-status-strip',
    requestReviewButton: 'knowledge-hub-request-review-button',
    markVerifiedButton: 'knowledge-hub-mark-verified-button',
    acknowledgeReadButton: 'knowledge-hub-acknowledge-read-button',
    outstandingReadersBadge: 'knowledge-hub-outstanding-readers-badge',
    requestReviewModal: 'knowledge-hub-request-review-modal',
    queueView: 'knowledge-hub-queue-view',
    queueRow: (docId: string) => `knowledge-hub-queue-row-${docId}`,
    changelogPanel: 'knowledge-hub-changelog-panel',
    changelogEntry: (index: number) => `knowledge-hub-changelog-entry-${index}`,
    /** @deprecated Standalone "Review changelog" toggle removed 2026-07-08; History lives on
     * Document Info dock segment (documentViewer.versionHistoryToggle). */
    changelogToggle: 'knowledge-hub-changelog-toggle',
    /** Document Info sidebar Knowledge Hub module */
    infoModule: 'knowledge-hub-info-module',
    infoStatusChip: 'knowledge-hub-info-status-chip',
    infoStatusSelect: 'knowledge-hub-info-status-select',
    infoIntervalSelect: 'knowledge-hub-info-interval-select',
    infoNextReview: 'knowledge-hub-info-next-review',
    infoApprovalRequiredSwitch: 'knowledge-hub-info-approval-required-switch',
    infoApproversPicker: 'knowledge-hub-info-approvers-picker',
    infoReadersPicker: 'knowledge-hub-info-readers-picker',
  },
  sessionImage: {
    viewer: 'session-image-viewer',
    thumb: 'session-image-thumb',
    caption: 'session-image-caption',
    lightbox: 'session-image-lightbox',
    lightboxCaption: 'session-image-lightbox-caption',
  },
  imageDocument: {
    viewer: 'image-document-viewer',
    image: 'image-document-image',
    download: 'image-document-download',
    copyUrl: 'image-document-copy-url',
    meta: 'image-document-meta',
  },
  /** react-markdown preview (thumbnails + image lightbox) */
  markdownPreview: {
    imageThumbnail: 'markdown-preview-image-thumbnail',
    imageLightbox: 'markdown-preview-image-lightbox',
    imageLightboxCaption: 'markdown-preview-image-lightbox-caption',
    readingBlock: 'markdown-preview-reading-block',
    readingGutter: 'markdown-preview-reading-gutter',
    readingGutterChat: 'markdown-preview-reading-gutter-chat',
    bookmarkButton: 'markdown-preview-bookmark-button',
    addToChatButton: 'markdown-preview-add-to-chat-button',
    taskCheckbox: 'markdown-preview-task-checkbox',
  },
  documentToc: {
    bookmarkIcon: 'document-toc-bookmark-icon',
    currentSectionPill: 'document-toc-current-section-pill',
  },
  /** ```yaml basecamp-draft``` fence: review card + copy for Basecamp comments */
  basecampDraftFence: {
    root: 'basecamp-draft-fence',
    copy: 'basecamp-draft-fence-copy',
    body: 'basecamp-draft-fence-body',
    target: 'basecamp-draft-fence-target',
    status: 'basecamp-draft-fence-status',
  },
  /** ```yaml status-board``` fence rendered as Hot pickups / Blocked */
  statusBoardFence: {
    root: 'status-board-fence-view',
    hotPickups: 'status-board-fence-hot-pickups',
    blocked: 'status-board-fence-blocked',
  },
  audioFencePlayer: {
    root: 'audio-fence-player',
    play: 'audio-fence-player-play',
    seek: 'audio-fence-player-seek',
    download: 'audio-fence-player-download',
    bookmark: 'audio-fence-player-bookmark',
    stale: 'audio-fence-player-stale',
    regenerate: 'audio-fence-player-regenerate',
    error: 'audio-fence-player-error',
    time: 'audio-fence-player-time',
  },
  ttsLessonAudioProto: {
    root: 'tts-lesson-audio-proto',
    sceneNav: 'tts-lesson-audio-proto-scene-nav',
    sceneButton: (id: string) => `tts-lesson-audio-proto-scene-${id}`,
    lessonBody: 'tts-lesson-audio-proto-lesson-body',
  },
  mermaid: {
    block: 'mermaid-block',
    chatBlock: 'mermaid-chat-block',
    diagram: 'mermaid-diagram',
    source: 'mermaid-source',
    error: 'mermaid-error',
    expandButton: 'mermaid-expand-button',
    toggleSourceButton: 'mermaid-toggle-source-button',
    chatButton: 'mermaid-chat-button',
    lightbox: 'mermaid-lightbox',
    lightboxDiagram: 'mermaid-lightbox-diagram',
    lightboxHint: 'mermaid-lightbox-hint',
  },
  imageSearchGallery: {
    root: 'image-search-gallery',
    card: (index: number) => `image-search-gallery-card-${index}`,
    insertButton: (index: number) => `image-search-gallery-insert-${index}`,
    sourceLink: (index: number) => `image-search-gallery-source-${index}`,
    overflowMenu: (index: number) => `image-search-gallery-overflow-${index}`,
    insertMenuItem: (index: number) => `image-search-gallery-insert-menu-${index}`,
    sourceMenuItem: (index: number) => `image-search-gallery-source-menu-${index}`,
  },
  /** One toggle per shell column. Header copies of these are retired after #panel-collapse ships. */
  shellToggle: {
    left: 'shell-panel-toggle-left',
    agent: 'shell-panel-toggle-agent',
  },
  resizableLayout: {
    container: 'resizable-layout',
    sidebar: 'resizable-layout-sidebar',
    sidebarToggle: 'resizable-layout-sidebar-toggle',
    mainArea: 'resizable-layout-main',
    chatPane: 'resizable-layout-chat-pane',
    chatPaneToggle: 'resizable-layout-chat-pane-toggle',
    documentListToggle: 'resizable-layout-document-list-toggle',
  },
  header: {
    root: 'header',
    wordmark: 'header-wordmark',
    search: 'header-search',
    userMenu: 'header-user-menu',
    accountButton: 'header-account-button',
    sidebarControls: 'header-sidebar-controls',
    leftSidebarToggle: 'header-left-sidebar-toggle',
    rightSidebarToggle: 'header-right-sidebar-toggle',
    helpMenu: 'header-help-menu',
    helpMenuContent: 'header-help-menu-content',
    takeTourButton: 'header-take-tour-button',
    keyboardShortcutsButton: 'header-keyboard-shortcuts',
    changelogLink: 'header-changelog-link',
    helpGuidesLink: 'header-help-guides-link',
    helpMcpLink: 'header-help-mcp-link',
  },
  productTour: {
    root: 'product-tour',
    overlay: 'product-tour-overlay',
    tooltip: 'product-tour-tooltip',
    nextButton: 'product-tour-next',
    backButton: 'product-tour-back',
    skipButton: 'product-tour-skip',
    doneButton: 'product-tour-done',
    gettingStartedLink: 'product-tour-getting-started',
    prompt: 'product-tour-prompt',
    promptStart: 'product-tour-prompt-start',
    promptSkip: 'product-tour-prompt-skip',
    anatomy: 'product-tour-anatomy',
    anatomyCaption: 'product-tour-anatomy-caption',
    anatomyRegion: (id: string) => `product-tour-anatomy-region-${id}`,
  },
  leftSidebar: {
    container: 'left-sidebar',
    iconBar: 'left-sidebar-icon-bar',
    panel: 'left-sidebar-panel',
    panelHeader: 'left-sidebar-panel-header',
    footer: 'left-sidebar-footer',
    panelMenuTrigger: 'left-sidebar-panel-menu-trigger',
    panelMenu: 'left-sidebar-panel-menu',
    panelMenuOpenTabsToggle: 'left-sidebar-panel-menu-open-tabs-toggle',
    panelMenuAutoHide: 'left-sidebar-panel-menu-auto-hide',
    panelMenuCollapse: 'left-sidebar-panel-menu-collapse',
    panelMenuRestoreAll: 'left-sidebar-panel-menu-restore-all',
    panelMenuTrash: 'left-sidebar-panel-menu-trash',
    panelMenuDensity: 'left-sidebar-panel-menu-density',
    workspaceScopeButton: 'left-sidebar-workspace-scope-button',
    workspaceScopePopover: 'left-sidebar-workspace-scope-popover',
    workspaceScopeRestoreAll: 'left-sidebar-workspace-scope-restore-all',
    workspaceScopeSelectAll: 'left-sidebar-workspace-scope-select-all',
    workspaceScopeFilter: 'left-sidebar-workspace-scope-filter',
    workspaceScopeAdd: 'left-sidebar-workspace-scope-add',
    workspaceScopeManage: 'left-sidebar-workspace-scope-manage',
    workspaceScopeShowMore: 'left-sidebar-workspace-scope-show-more',
    settingsIcon: 'left-sidebar-settings-icon',
    /** Core modes above the rail divider (Browse, Inbox, Flows, Chats) */
    iconBarCore: 'left-sidebar-icon-bar-core',
    /** Extension icons + add slot below the core-mode divider */
    iconBarExtensions: 'left-sidebar-icon-bar-extensions',
    /** Hover / tour: add extension (+) in the next rail slot */
    railAddExtension: 'left-sidebar-rail-add-extension',
    expandRailButton: 'left-sidebar-expand-rail',
    workspacesBrowseHeading: 'left-sidebar-workspaces-browse-heading',
    /** Documents row under a workspace (context menu for new file/folder at root) */
    workspaceDocumentsRow: (workspaceId: string) =>
      `left-sidebar-workspace-${workspaceId}-documents-row`,
    workspaceMyDocumentsRow: (workspaceId: string) =>
      `left-sidebar-workspace-${workspaceId}-my-documents-row`,
    /** Hoisted so HMR cannot leave a stale string/undefined on the object. */
    workspacePeopleRow: leftSidebarWorkspacePeopleRow,
    workspacePinnedParentRow: (workspaceId: string) =>
      `left-sidebar-workspace-${workspaceId}-pinned-parent-row`,
    workspacePinnedItemRow: (workspaceId: string, shortcutId: string) =>
      `left-sidebar-workspace-${workspaceId}-pinned-item-${shortcutId}`,
    railBasecamp: 'left-sidebar-rail-basecamp',
    railNextcloud: 'left-sidebar-rail-nextcloud',
    railSuggested: (id: string) => `left-sidebar-rail-${id}-suggested`,
    workspaceRowSettings: (workspaceId: string) =>
      `left-sidebar-workspace-row-settings-${workspaceId}`,
    workspaceRowHide: (workspaceId: string) =>
      `left-sidebar-workspace-row-hide-${workspaceId}`,
    workspaceRowHideOthers: (workspaceId: string) =>
      `left-sidebar-workspace-row-hide-others-${workspaceId}`,
    workspaceBasecampRow: (workspaceId: string) =>
      `left-sidebar-workspace-${workspaceId}-basecamp-row`,
    workspaceBasecampAuthBadge: (workspaceId: string) =>
      `left-sidebar-workspace-${workspaceId}-basecamp-auth-badge`,
    workspaceBasecampContextMenu: (workspaceId: string) =>
      `left-sidebar-workspace-${workspaceId}-basecamp-context-menu`,
    workspaceKnowledgeHubRow: (workspaceId: string) =>
      `left-sidebar-workspace-${workspaceId}-knowledge-hub-row`,
    workspaceKnowledgeHubReviewQueue: (workspaceId: string) =>
      `left-sidebar-workspace-${workspaceId}-knowledge-hub-review-queue`,
    workspaceKnowledgeHubNeedsReview: (workspaceId: string) =>
      `left-sidebar-workspace-${workspaceId}-knowledge-hub-needs-review`,
    googleDriveImportsSection: 'left-sidebar-google-drive-imports-section',
    googleDriveImportsRow: (docId: string) =>
      `left-sidebar-google-drive-imports-row-${docId}`,
    openTabsSection: 'left-sidebar-open-tabs-section',
    openTabsResizeHandle: 'left-sidebar-open-tabs-resize-handle',
    openTabsRow: (tabId: string) => `left-sidebar-open-tabs-row-${tabId}`,
    openTabsShowWorkspace: (workspaceId: string) =>
      `left-sidebar-open-tabs-show-workspace-${workspaceId}`,
    openTabsCloseAll: (workspaceId: string) =>
      `left-sidebar-open-tabs-close-all-${workspaceId}`,
  },
  /** Canonical WorkspaceScopeSelector (search / chat / secrets) */
  workspaceScopeSelector: {
    selectAll: 'workspace-scope-select-all',
    showMore: 'workspace-scope-show-more',
  },
  inboxView: {
    container: 'inbox-view',
    toolbar: 'inbox-view-toolbar',
    feed: 'inbox-feed',
    item: (id: string) => `inbox-item-${id}`,
  },
  /** Split inbox prototype (test-inbox-split) */
  inboxSplitPrototype: {
    readerViewFlowButton: 'inbox-split-reader-view-flow',
  },
  /** Inbox folders continuity prototype (?test=inbox-folders) */
  inboxFoldersPrototype: {
    root: 'inbox-folders-prototype',
    appHeader: 'inbox-folders-app-header',
    iconRail: 'inbox-folders-icon-rail',
    foldersRail: 'inbox-folders-rail',
    tabRow: 'inbox-folders-tab-row',
    titleRow: 'inbox-folders-title-row',
    navBack: 'inbox-folders-nav-back',
    navForward: 'inbox-folders-nav-forward',
    threadList: 'inbox-folders-thread-list',
    previewToggle: 'inbox-folders-preview-toggle',
    previewPanel: 'inbox-folders-preview-panel',
    readerBack: 'inbox-folders-reader-back',
    readerRoot: 'inbox-folders-reader-root',
    prototypeIndex: 'inbox-folders-prototype-index',
  },
  /** Matter Feed prototype (?test=matter-feed) */
  matterFeedPrototype: {
    root: 'matter-feed-prototype',
    appHeader: 'matter-feed-app-header',
    prototypeIndex: 'matter-feed-prototype-index',
    feedMenuTrigger: 'matter-feed-menu-trigger',
    feedMenu: 'matter-feed-menu',
    refineInChat: 'matter-feed-refine-in-chat',
    vsInboxBanner: 'matter-feed-vs-inbox-banner',
    topicChips: 'matter-feed-topic-chips',
    topicChip: (id: string) => `matter-feed-topic-${id}`,
    batchMeta: 'matter-feed-batch-meta',
    enoughButton: 'matter-feed-enough',
    reader: 'matter-feed-reader',
    readerClose: 'matter-feed-reader-close',
    cardList: 'matter-feed-card-list',
    skeleton: 'matter-feed-skeleton',
    batchComplete: 'matter-feed-batch-complete',
    emptyState: 'matter-feed-empty',
    card: (id: string) => `matter-feed-card-${id}`,
    cardTitle: (id: string) => `matter-feed-card-title-${id}`,
    thumb: (id: string) => `matter-feed-thumb-${id}`,
    cardMenuTrigger: (id: string) => `matter-feed-card-menu-trigger-${id}`,
    cardMenu: (id: string) => `matter-feed-card-menu-${id}`,
    provenance: (id: string) => `matter-feed-provenance-${id}`,
    signalMore: (id: string) => `matter-feed-signal-more-${id}`,
    signalLess: (id: string) => `matter-feed-signal-less-${id}`,
    signalSave: (id: string) => `matter-feed-signal-save-${id}`,
    signalDismiss: (id: string) => `matter-feed-signal-dismiss-${id}`,
    spawnSource: (id: string) => `matter-feed-spawn-source-${id}`,
    spawnNote: (id: string) => `matter-feed-spawn-note-${id}`,
    spawnTodo: (id: string) => `matter-feed-spawn-todo-${id}`,
    toast: 'matter-feed-toast',
  },
  /** Production Chats sidebar mode */
  chatsSidebar: {
    container: 'chats-sidebar',
    search: 'chats-sidebar-search',
    list: 'chats-sidebar-list',
    newChat: 'chats-sidebar-new-chat',
    refresh: 'chats-sidebar-refresh',
    panelMenuTrigger: 'chats-sidebar-panel-menu-trigger',
    panelMenu: 'chats-sidebar-panel-menu',
    feedbackMenuItem: 'chats-sidebar-feedback',
    item: (threadId: string) => `chats-sidebar-item-${threadId}`,
    workspaceTags: (threadId: string) => `chats-sidebar-workspace-tags-${threadId}`,
    workspaceTagOverflow: (threadId: string) => `chats-sidebar-workspace-tag-overflow-${threadId}`,
    contextMenu: 'chats-sidebar-context-menu',
    sectionMore: (bucket: string) => `chats-sidebar-section-more-${bucket}`,
    sectionHeader: (bucket: string) => `chats-sidebar-section-header-${bucket}`,
    loadMoreLoading: 'chats-sidebar-load-more-loading',
    pinnedSection: 'chats-sidebar-pinned-section',
    archivedToggle: 'chats-sidebar-archived-toggle',
    loadMoreSentinel: 'chats-sidebar-load-more-sentinel',
  },
  /** Shared RenameField (AI suggest + prefix chips) */
  renameField: {
    root: 'rename-field',
    input: 'rename-field-input',
    suggest: 'rename-field-suggest',
    chips: 'rename-field-chips',
    dateChip: 'rename-field-date-chip',
    todoChip: 'rename-field-todo-chip',
    uniqChip: 'rename-field-uniq-chip',
  },
  chatMainTab: {
    container: 'chat-main-tab',
    loading: 'chat-main-tab-loading',
  },
  chatConversationToolbar: {
    root: 'chat-conversation-toolbar',
    title: 'chat-conversation-toolbar-title',
    menuButton: 'chat-conversation-toolbar-menu-button',
    feedbackButton: 'chat-conversation-toolbar-feedback-button',
  },
  conversationMoreMenu: {
    feedback: 'conversation-more-menu-feedback',
    reportBug: 'conversation-more-menu-report-bug',
    agentIssue: 'conversation-more-menu-agent-issue',
    learnFromSession: 'conversation-more-menu-learn-from-session',
    sessionFiles: 'conversation-more-menu-session-files',
    copyTranscript: 'conversation-more-menu-copy-transcript',
  },
  feedbackModal: {
    root: 'feedback-modal',
    submit: 'feedback-modal-submit',
    category: 'feedback-modal-category',
    description: 'feedback-modal-description',
    expected: 'feedback-modal-expected',
    actual: 'feedback-modal-actual',
    rating: 'feedback-modal-rating',
    targetFolder: 'feedback-modal-target-folder',
    sessionContext: 'feedback-modal-session-context',
    filing: 'feedback-modal-filing',
    success: 'feedback-modal-success',
    error: 'feedback-modal-error',
    retry: 'feedback-modal-retry',
    screenshotDropzone: 'feedback-modal-screenshot-dropzone',
    screenshotFileInput: 'feedback-modal-screenshot-file-input',
    screenshotThumbnail: 'feedback-modal-screenshot-thumbnail',
    screenshotRemoveButton: 'feedback-modal-screenshot-remove',
  },
  /** Agents chat layout prototype (?test=agents-layout) */
  agentsLayoutPrototype: {
    root: 'agents-layout-prototype',
    agentsSidebar: 'agents-layout-agents-sidebar',
    agentsSearch: 'agents-layout-agents-search',
    agentsList: 'agents-layout-agents-list',
    agentsNewChat: 'agents-layout-agents-new-chat',
    agentsPanelMenu: 'agents-layout-agents-panel-menu',
    editorTabs: 'agents-layout-editor-tabs',
    editorContent: 'agents-layout-editor-content',
    editorDocument: 'agents-layout-editor-document',
    editorChat: 'agents-layout-editor-chat',
    agentPanel: 'agents-layout-agent-panel',
    agentPanelChat: 'agents-layout-agent-panel-chat',
    qaChecklist: 'agents-layout-qa-checklist',
    backToPrototypeIndex: 'agents-layout-back-prototype-index',
  },
  /** Streaming agent UI redesign prototype (?test=streaming-agent) */
  streamingAgentPrototype: {
    root: 'streaming-agent-prototype',
    currentStream: 'streaming-agent-current-stream',
    minimalStream: 'streaming-agent-minimal-stream',
    minimalEditRow: 'streaming-agent-minimal-edit-row',
    midTurnShimmer: 'streaming-agent-mid-turn-shimmer',
    designNotes: 'streaming-agent-design-notes',
    backToPrototypeIndex: 'streaming-agent-back-prototype-index',
    softErrorsScenario: 'streaming-agent-scenario-soft-errors',
  },
  /** Flows Studio prototype (?test=flows-studio) */
  flowsStudioPrototype: {
    root: 'flows-studio-prototype',
    surfaceNav: 'flows-studio-surface-nav',
    pulseFeed: 'flows-studio-pulse-feed',
    pulseStatBar: 'flows-studio-pulse-stat-bar',
    pulseStat: (id: string) => `flows-studio-pulse-stat-${id}`,
    pulseUpNext: 'flows-studio-pulse-up-next',
    pulseUpNextItem: (id: string) => `flows-studio-pulse-up-next-${id}`,
    pulseActivity: 'flows-studio-pulse-activity',
    pulseListHead: 'flows-studio-pulse-list-head',
    pulseNeedsAttention: 'flows-studio-pulse-needs-attention',
    pulseRunRow: (id: string) => `flows-studio-pulse-run-${id}`,
    pulseRunOverflow: 'flows-studio-pulse-run-overflow',
    pulseMain: 'flows-studio-pulse-main',
    pulseRunContextMenu: 'flows-studio-pulse-run-context-menu',
    pulseScope: 'flows-studio-pulse-scope',
    pulseSearch: 'flows-studio-pulse-search',
    pulseSearchToggle: 'flows-studio-pulse-search-toggle',
    pulseFilterMenu: 'flows-studio-pulse-filter',
    pulseSort: 'flows-studio-pulse-sort',
    pulseViewMode: 'flows-studio-pulse-view-mode',
    pulseUpNextMore: 'flows-studio-pulse-up-next-more',
    pulseRunDetail: 'flows-studio-pulse-run-detail',
    pulsePreviewRuns: 'flows-studio-pulse-preview-runs',
    flowTreeOverflow: 'flows-studio-flow-tree-overflow',
    flowTreeContextMenu: 'flows-studio-flow-tree-context-menu',
    flowFolder: 'flows-studio-flow-folder',
    flowFolderRow: (id: string) => `flows-studio-flow-folder-row-${id}`,
    flowFolderPreview: 'flows-studio-flow-folder-preview',
    flowFolderPreviewHeader: 'flows-studio-flow-folder-preview-header',
    builder: 'flows-studio-builder',
    flowDocument: 'flows-studio-flow-document',
    flowDocumentContextMenu: 'flows-studio-flow-document-context-menu',
    flowDocumentTabs: 'flows-studio-flow-document-tabs',
    flowDocumentRuns: 'flows-studio-flow-document-runs',
    flowRunSummary: 'flows-studio-flow-run-summary',
    flowDocumentInfo: 'flows-studio-flow-document-info',
    flowDocumentInfoButton: 'flows-studio-flow-document-info-button',
    flowDocumentRuntime: 'flows-studio-flow-document-runtime',
    flowDocumentRunning: 'flows-studio-flow-document-running',
    flowDocumentMenu: 'flows-studio-flow-document-menu',
    flowDocumentRunNow: 'flows-studio-flow-document-run-now',
    flowDocumentRefine: 'flows-studio-flow-document-refine',
    flowDocumentProposal: 'flows-studio-flow-document-proposal',
    flowPipeline: 'flows-studio-flow-pipeline',
    propertyRail: 'flows-studio-property-rail',
    flowInspectorPrompt: 'flows-studio-flow-inspector-prompt',
    flowInspectorSuggest: 'flows-studio-flow-inspector-suggest',
    flowInspectorSuggestion: 'flows-studio-flow-inspector-suggestion',
    flowInspectorAsk: 'flows-studio-flow-inspector-ask',
    flowAgentMark: 'flows-studio-flow-agent-mark',
    bindingPicker: 'flows-studio-binding-picker',
    simulateButton: 'flows-studio-simulate-button',
    gallery: 'flows-studio-gallery',
    galleryWorkspaceTarget: 'flows-studio-gallery-workspace-target',
    galleryInstallButton: 'flows-studio-gallery-install',
    /** @deprecated use sourcesCatalog - kept for older screenshots */
    triggerCatalog: 'flows-studio-trigger-catalog',
    sourcesCatalog: 'flows-studio-sources-catalog',
    webhookPanel: 'flows-studio-webhook-panel',
    composer: 'flows-studio-composer',
    composerRefine: 'flows-studio-composer-refine',
    composerAgentPanel: 'flows-studio-composer-agent-panel',
    flowSessionFiles: 'flows-studio-session-files',
    flowSessionFilesToggle: 'flows-studio-session-files-toggle',
    flowSessionFile: (id: string) => `flows-studio-session-file-${id}`,
    flowSessionTranscriptClear: 'flows-studio-session-transcript-clear',
    prunedTree: 'flows-studio-pruned-tree',
    modePanelOverflow: 'flows-studio-mode-panel-overflow',
    backToPrototypeIndex: 'flows-studio-back-prototype-index',
    /** App-shell chrome (aligned to production Header / tabs / title row) */
    appHeader: 'flows-studio-app-header',
    iconRail: 'flows-studio-icon-rail',
    tabRow: 'flows-studio-tab-row',
    tab: (id: string) => `flows-studio-tab-${id}`,
    titleRow: 'flows-studio-title-row',
    backButton: 'flows-studio-back-button',
    forwardButton: 'flows-studio-forward-button',
  },
  /** Flow editor prototype (?test=flow-editor) */
  flowEditorPrototype: {
    root: 'flow-editor-prototype',
    workspaceScopeBar: 'flow-editor-workspace-scope',
    surfaceTabs: 'flow-editor-surface-tabs',
    managerTable: 'flow-editor-manager-table',
    editorPanel: 'flow-editor-editor-panel',
    jobHistory: 'flow-editor-job-history',
    recipeGallery: 'flow-editor-recipe-gallery',
    backToPrototypeIndex: 'flow-editor-back-prototype-index',
    backToInboxSplit: 'flow-editor-back-inbox-split',
  },
  advancedSearchView: {
    root: 'search-view',
    input: 'search-view-input',
    results: 'search-view-results',
    searchError: 'search-view-search-error',
    leftRail: 'search-view-left-rail',
    titleRow: 'search-view-title-row',
    filterRow: 'search-view-filter-row',
    viewMode: 'search-view-view-mode',
    listHeader: 'search-view-list-header',
    filtersButton: 'search-view-filters-button',
    userFilter: 'search-view-user-filter',
    previewToggle: 'search-view-preview-toggle',
    previewPane: 'search-view-preview-pane',
    previewResize: 'search-view-preview-resize',
    pathChip: 'search-view-path-chip',
    statusBar: 'search-view-status-bar',
    statusBarHelp: 'search-view-status-bar-help',
    statusBarMeta: 'search-view-status-bar-meta',
    queryStats: 'search-view-query-stats',
    headerSlot: 'search-view-header-slot',
    footerSlot: 'search-view-footer-slot',
    backButton: 'search-view-back',
    forwardButton: 'search-view-forward',
    menuButton: 'search-view-menu',
  },
  activityView: {
    root: 'activity-view',
    list: 'activity-view-list',
    membersStrip: 'activity-view-members',
    membersTrigger: 'activity-view-members-trigger',
    previewToggle: 'activity-view-preview-toggle',
    statusBar: 'activity-view-status-bar',
    statusBarHelp: 'activity-view-status-bar-help',
  },
  /** Legacy alias group; live UI uses AdvancedSearchView + advancedSearchView keys */
  searchView: {
    container: 'search-view',
    input: 'search-view-input',
    results: 'search-view-results',
  },
  tagsView: {
    container: 'tags-view',
    list: 'tags-list',
  },
  trashView: {
    container: 'trash-view',
    searchInput: 'trash-view-search',
    list: 'trash-view-list',
  },
  workspaceHome: {
    root: 'workspace-home',
    newDocument: 'workspace-home-new-document',
    newFolder: 'workspace-home-new-folder',
    previewToggle: 'workspace-home-preview-toggle',
    previewPane: 'workspace-home-preview-pane',
    previewEmpty: 'workspace-home-preview-empty',
  },
  /** "Active workspaces" chip row shared by the global Home page and the
   * per-workspace dashboard's quick-switcher (see workspace-chip-row.tsx). */
  homeWorkspaceChips: {
    row: 'home-workspace-chips-row',
    chip: (workspaceId: string) => `home-workspace-chips-chip-${workspaceId}`,
  },
  /** Global Home (`/`) widget surfaces */
  home: {
    recentChatsFeed: 'home-recent-chats-feed',
    flowsFeed: 'home-flows-feed',
    inboxFeed: 'home-inbox-feed',
    recentActivity: 'home-recent-activity',
    smartSuggestions: 'home-smart-suggestions',
    quickActions: 'home-quick-actions',
    basecampBookmarks: 'home-basecamp-bookmarks',
    basecampBookmarksRefresh: 'home-basecamp-bookmarks-refresh',
    basecampProjects: 'home-basecamp-projects',
    basecampProjectsRefresh: 'home-basecamp-projects-refresh',
    widgetShell: 'home-widget-shell',
    chromeBar: 'home-chrome-bar',
    statusBar: 'home-status-bar',
    statusBarHelp: 'home-status-bar-help',
    previewToggle: 'home-preview-toggle',
    previewPane: 'home-preview-pane',
    previewEmpty: 'home-preview-empty',
    summaryPreview: 'home-summary-preview',
    summaryPreviewMarkdown: 'home-summary-preview-markdown',
    flowPreviewDetails: 'home-flow-preview-details',
    /** QuizDocumentRenderer embedded in Home / list preview pane */
    quizPreview: 'home-quiz-preview',
    contextMenu: 'home-feed-context-menu',
    contextMenuOpen: 'home-feed-context-menu-open',
    contextMenuOpenInNewTab: 'home-feed-context-menu-open-in-new-tab',
    contextMenuOpenInAgentPanel: 'home-feed-context-menu-open-in-agent-panel',
    contextMenuPreview: 'home-feed-context-menu-preview',
    contextMenuAddToChat: 'home-feed-context-menu-add-to-chat',
  },
  workspaceIndicator: {
    root: 'workspace-indicator',
    /** Dropdown panel (workspace toggles) */
    menu: 'workspace-indicator-menu',
  },
  /** Browse / projects tree (sidebar projects view) */
  browseView: {
    container: 'browse-view',
  },
  explorerSection: {
    container: 'explorer-section',
    breadcrumbs: 'explorer-section-breadcrumbs',
    tree: 'explorer-section-tree',
    importTextButton: 'explorer-section-import-text',
    emptyImportTextButton: 'explorer-section-empty-import-text',
    emptyNewMenu: 'explorer-section-empty-new',
    emptyActions: 'explorer-section-empty-actions',
  },
  localTextImport: {
    contextMenuItem: 'local-text-import-context-menu-item',
    folderDropTarget: (path: string) =>
      `local-text-import-folder-drop-${path.replace(/[^a-zA-Z0-9_.-]/g, '_')}`,
  },
  explorerNode: {
    /** Stable DOM id from tree path (slashes → underscores) */
    row: (path: string) =>
      `explorer-node-${path.replace(/[^a-zA-Z0-9_.-]/g, '_')}`,
    levelFocusButton: (parentPath: string, mode: 'collapse' | 'expand') =>
      `explorer-level-focus-${mode}-${parentPath.replace(/[^a-zA-Z0-9_.-]/g, '_')}`,
    levelFocusExpandLink: (parentPath: string) =>
      `explorer-level-focus-expand-${parentPath.replace(/[^a-zA-Z0-9_.-]/g, '_')}`,
  },
  workspaceSearchBadge: {
    root: 'workspace-search-badge',
  },
  inbox: {
    page: 'inbox-page',
    browseRail: 'inbox-browse-rail',
    feed: 'inbox-feed',
    reader: 'inbox-reader',
    readerHeader: 'inbox-reader-header',
    /** Folders list-surface (production webapp) */
    foldersModePanel: 'inbox-folders-mode-panel',
    foldersIndex: 'inbox-folders-index',
    foldersIndexToolbar: 'inbox-folders-index-toolbar',
    foldersThreadList: 'inbox-folders-thread-list',
    foldersStatusBar: 'inbox-folders-status-bar',
    foldersStatusBarHelp: 'inbox-folders-status-bar-help',
    foldersPreviewToggle: 'inbox-folders-preview-toggle',
    foldersPreviewPanel: 'inbox-folders-preview-panel',
    foldersPreviewResize: 'inbox-folders-preview-resize',
    foldersGroupToggle: 'inbox-folders-group-toggle',
    foldersGroupHeader: (workspaceKey: string) => `inbox-folders-group-${workspaceKey}`,
    foldersFilterBar: 'inbox-folders-filter-bar',
    foldersFilterChips: 'inbox-folders-filter-chips',
    foldersFiltersMenu: 'inbox-folders-filters-menu',
    foldersSearchBar: 'inbox-folders-search-bar',
    folderRow: (folderId: string) => `inbox-folder-row-${folderId}`,
    /** Markdown body for flow / system inbox messages (doc links, summary prose) */
    readerMarkdown: 'inbox-reader-markdown',
    sidebarPanel: 'inbox-sidebar-panel',
    contextMenu: 'inbox-thread-context-menu',
    threadActionsMenu: 'inbox-thread-actions-menu',
    threadMainTab: 'inbox-thread-main-tab',
    /** Continuity chrome for stranded /inbox/thread reader */
    threadToolbar: 'inbox-thread-toolbar',
    threadToolbarTitle: 'inbox-thread-toolbar-title',
    threadToolbarReply: 'inbox-thread-toolbar-reply',
    readerAddToChat: 'inbox-reader-add-to-chat',
    /** Slim source/workspace line when title/actions live on the toolbar */
    readerMeta: 'inbox-reader-meta',
    /** GitSource upstream conflict banner (Phase 5) */
    gitConflictBar: 'inbox-git-conflict-bar',
    gitConflictAcceptUpstream: 'inbox-git-conflict-accept-upstream',
    gitConflictKeepFlowNotes: 'inbox-git-conflict-keep-flownotes',
    gitConflictViewDiff: 'inbox-git-conflict-view-diff',
    /** Soul evolution Phase 2 propose/approve bar */
    soulProposalBar: 'inbox-soul-proposal-bar',
    soulProposalApprove: 'inbox-soul-proposal-approve',
    soulProposalDismiss: 'inbox-soul-proposal-dismiss',
    soulProposalViewDiff: 'inbox-soul-proposal-view-diff',
    soulProposalEdit: 'inbox-soul-proposal-edit',
    /** Discuss escape hatch (opens bound chat with soul doc chip) */
    soulProposalDiscuss: 'inbox-soul-proposal-discuss',
    soulProposalFooter: 'inbox-soul-proposal-footer',
    soulProposalDocLink: 'inbox-soul-proposal-doc-link',
    soulProposalDecisionEvent: 'inbox-soul-proposal-decision-event',
    soulProposalDecisionDocLink: 'inbox-soul-proposal-decision-doc-link',
    soulProposalTimeline: 'inbox-soul-proposal-timeline',
    documentEvent: 'inbox-document-event',
    documentEventDocLink: 'inbox-document-event-doc-link',
    agentStreamTerminal: 'agent-stream-terminal',
    agentStreamAuthError: 'agent-stream-auth-error',
    agentStreamHttpError: 'agent-stream-http-error',
    agentPrLink: 'agent-pr-link',
    agentDispatchPanel: 'agent-dispatch-panel',
    agentWatchLive: 'agent-watch-live',
    agentViewInInbox: 'agent-view-in-inbox',
    agentWatchLiveDialog: 'agent-watch-live-dialog',
    agentThreadMessages: 'agent-thread-messages',
    /** PanelRight preview for Inbox Chats folder (linked ai_conversation messages) */
    chatPreview: 'inbox-chat-preview',
    inviteAcceptButton: 'inbox-invite-accept-button',
    passwordSetupBanner: 'inbox-password-setup-banner',
    passwordSetupButton: 'inbox-password-setup-button',
    passwordSetupDismiss: 'inbox-password-setup-dismiss',
  },
  cloudDispatch: {
    banner: 'cloud-dispatch-banner',
    viewMrLink: 'cloud-dispatch-view-mr',
    watchLiveButton: 'cloud-dispatch-watch-live',
    dispatchButton: 'cloud-dispatch-button',
  },
  /** Product Board (?test=product-management / sidebar route) */
  productBoard: {
    root: 'product-board',
    tabDashboard: 'product-board-tab-dashboard',
    tabRoadmap: 'product-board-tab-roadmap',
    tabStatusBoard: 'product-board-tab-status-board',
    previewToggle: 'product-board-preview-toggle',
    betPreviewEmpty: 'product-board-bet-preview-empty',
    betPreviewActions: 'product-board-bet-preview-actions',
    betPreviewOpen: 'product-board-bet-preview-open',
    roadmapDocPreviewActions: 'product-board-roadmap-doc-preview-actions',
    roadmapColumnOpenPreview: (sectionId: string) =>
      `product-board-roadmap-open-preview-${sectionId}`,
    roadmapSkeleton: 'product-board-roadmap-skeleton',
    roadmapPanelSearch: 'product-board-roadmap-panel-search',
    roadmapFilterOpen: 'product-board-roadmap-filter-open',
    roadmapFiltersToggle: 'product-board-roadmap-filters-toggle',
    roadmapBoardFilters: 'product-board-roadmap-board-filters',
    roadmapFilterMvpChip: 'product-board-roadmap-filter-mvp-critical',
    roadmapFilterDispatchChip: 'product-board-roadmap-filter-dispatchable',
    roadmapFilterClear: 'product-board-roadmap-filter-clear',
    roadmapFilterCycle: 'product-board-roadmap-filter-cycle',
    roadmapFilterTag: (tag: string) =>
      `product-board-roadmap-filter-tag-${tag.replace(/[^a-z0-9-]/gi, '-')}`,
    /** Popover/control for picking additional roadmap filter tags */
    roadmapFilterTagsMore: 'product-board-roadmap-filter-tags-more',
    dashboardBetCard: (id: string) => `product-board-dashboard-bet-${id}`,
    statusBar: 'product-board-status-bar',
  },
  accountSettings: {
    section: 'account-settings',
    profileCard: 'account-settings-profile',
    displayNameInput: 'account-settings-display-name-input',
    displayNameSaveButton: 'account-settings-display-name-save',
    emailAddressesCard: 'account-settings-email-addresses',
    addEmailInput: 'account-settings-add-email-input',
    addEmailButton: 'account-settings-add-email-button',
    addEmailError: 'account-settings-add-email-error',
    secondaryEmailRow: (id: string) => `account-settings-email-row-${id}`,
    passwordCard: 'account-settings-password-card',
    passwordInput: 'account-settings-password-input',
    passwordConfirmInput: 'account-settings-password-confirm-input',
    passwordSubmitButton: 'account-settings-password-submit',
    agentsMcpTip: 'account-settings-agents-mcp-tip',
    agentsMcpGuideLink: 'account-settings-agents-mcp-guide-link',
  },
  workspaceSettings: {
    cursorExtensionVsixDownload: 'workspace-settings-cursor-extension-vsix-download',
    inviteEmailInput: 'workspace-settings-invite-email',
    inviteSubmitButton: 'workspace-settings-invite-submit',
    pendingInvitesSection: 'workspace-settings-pending-invites',
    pendingInviteRow: (id: string) => `workspace-settings-pending-invite-row-${id}`,
    pendingInviteOpenLink: (id: string) => `workspace-settings-pending-invite-open-${id}`,
    pendingInviteCopyLink: (id: string) => `workspace-settings-pending-invite-copy-${id}`,
    deleteSection: 'workspace-settings-delete-section',
    deleteTrigger: 'workspace-settings-delete-trigger',
    deleteDialog: 'workspace-settings-delete-dialog',
    deleteConfirmInput: 'workspace-settings-delete-confirm-input',
    deleteConfirmButton: 'workspace-settings-delete-confirm-button',
    googleDriveConnectButton: 'workspace-settings-google-drive-connect',
    googleDriveDisconnectButton: 'workspace-settings-google-drive-disconnect',
    googleDrivePublishTemplateInput: 'workspace-settings-google-drive-publish-template-input',
    googleDrivePublishTemplateSave: 'workspace-settings-google-drive-publish-template-save',
    googleDrivePublishTemplateClear: 'workspace-settings-google-drive-publish-template-clear',
    googleDrivePublishTemplateLink: 'workspace-settings-google-drive-publish-template-link',
    basecampPanel: 'workspace-settings-basecamp-panel',
    basecampConnectButton: 'workspace-settings-basecamp-connect',
    basecampListeningPanel: 'workspace-settings-basecamp-listening-panel',
    basecampListeningFilter: 'workspace-settings-basecamp-listening-filter',
    basecampProjectMappingsPanel: 'workspace-settings-basecamp-project-mappings-panel',
    basecampProjectMappingsDefaultInput: 'workspace-settings-basecamp-project-mappings-default-input',
    basecampProjectMappingsSave: 'workspace-settings-basecamp-project-mappings-save',
    basecampProjectMappingsAddTrigger: 'workspace-settings-basecamp-project-mappings-add-trigger',
    basecampProjectMappingsAddFilter: 'workspace-settings-basecamp-project-mappings-add-filter',
    basecampProjectMappingsReadOnlyNote: 'workspace-settings-basecamp-project-mappings-read-only',
    basecampProjectMappingsOverride: (projectId: string) =>
      `workspace-settings-basecamp-project-mappings-override-${projectId}`,
    basecampProjectMappingsOverrideToggle: (projectId: string) =>
      `workspace-settings-basecamp-project-mappings-override-toggle-${projectId}`,
    basecampProjectMappingsOverrideFolder: (projectId: string) =>
      `workspace-settings-basecamp-project-mappings-override-folder-${projectId}`,
    basecampProjectMappingsOverrideRemove: (projectId: string) =>
      `workspace-settings-basecamp-project-mappings-override-remove-${projectId}`,
    basecampProjectMappingsOverrideRemoveDialog: (projectId: string) =>
      `workspace-settings-basecamp-project-mappings-override-remove-dialog-${projectId}`,
    basecampProjectMappingsOverrideRemoveConfirm: (projectId: string) =>
      `workspace-settings-basecamp-project-mappings-override-remove-confirm-${projectId}`,
    basecampProjectMappingsOverrideUndoRow: (projectId: string) =>
      `workspace-settings-basecamp-project-mappings-override-undo-row-${projectId}`,
    basecampProjectMappingsOverrideUndo: (projectId: string) =>
      `workspace-settings-basecamp-project-mappings-override-undo-${projectId}`,
    basecampProjectMappingsRuleAdd: (projectId: string) =>
      `workspace-settings-basecamp-project-mappings-rule-add-${projectId}`,
    basecampProjectMappingsRuleList: (projectId: string, index: number) =>
      `workspace-settings-basecamp-project-mappings-rule-list-${projectId}-${index}`,
    basecampProjectMappingsRuleFolder: (projectId: string, index: number) =>
      `workspace-settings-basecamp-project-mappings-rule-folder-${projectId}-${index}`,
    basecampProjectMappingsRuleRemove: (projectId: string, index: number) =>
      `workspace-settings-basecamp-project-mappings-rule-remove-${projectId}-${index}`,
    zoomPanel: 'workspace-settings-zoom-panel',
    zoomConnectButton: 'workspace-settings-zoom-connect',
    zoomValidateButton: 'workspace-settings-zoom-validate',
    zoomDisconnectButton: 'workspace-settings-zoom-disconnect',
    breadcrumbs: 'workspace-settings-breadcrumbs',
    subNav: 'workspace-settings-sub-nav',
    sectionOverview: 'workspace-settings-section-overview',
    renameInput: 'workspace-settings-rename-input',
    renameSave: 'workspace-settings-rename-save',
    renameReadonly: 'workspace-settings-rename-readonly',
    sectionExtensions: 'workspace-settings-section-extensions',
    sectionTeam: 'workspace-settings-section-team',
    sectionKeys: 'workspace-settings-section-keys',
    sectionUsage: 'workspace-settings-section-usage',
    usageUserList: 'workspace-settings-usage-user-list',
    sectionDanger: 'workspace-settings-section-danger',
    llmPolicy: 'workspace-settings-llm-policy',
    llmDefaultProvider: 'workspace-settings-llm-default-provider',
    llmFallbackProvider: 'workspace-settings-llm-fallback-provider',
    llmDefaultModel: 'workspace-settings-llm-default-model',
    basecampIngestionProvider: 'workspace-settings-basecamp-ingestion-provider',
    extensionPage: (id: string) => `workspace-settings-extension-${id}`,
    configureExtension: (id: string) => `workspace-settings-configure-${id}`,
    smtpSendTestTo: 'workspace-settings-smtp-send-test-to',
    smtpSendTest: (connectionName: string) =>
      `workspace-settings-smtp-send-test-${connectionName}`,
    smtpSendTestError: 'workspace-settings-smtp-send-test-error',
    uptimeSection: 'workspace-settings-uptime-section',
    uptimeApiKey: 'workspace-settings-uptime-api-key',
    uptimeSaveToken: 'workspace-settings-uptime-save-token',
    uptimeTestConnection: 'workspace-settings-uptime-test-connection',
    uptimeStatusMessage: 'workspace-settings-uptime-status-message',
    slackSection: 'workspace-settings-slack-section',
    slackBotToken: 'workspace-settings-slack-bot-token',
    slackChannelId: 'workspace-settings-slack-channel-id',
    slackSaveSettings: 'workspace-settings-slack-save-settings',
    slackTestConnection: 'workspace-settings-slack-test-connection',
    slackStatusMessage: 'workspace-settings-slack-status-message',
  },
  settings: {
    chatSkills: 'settings-chat-skills',
    chatSkillsList: 'settings-chat-skills-list',
    chatSkillsRow: (id: string) => `settings-chat-skills-row-${id}`,
    chatSkillsCreateLink: 'settings-chat-skills-create-link',
    searchInput: 'settings-search-input',
    searchResults: 'settings-search-results',
    searchResult: (id: string) => `settings-search-result-${id}`,
  },
  workspaceSettingsIaTest: {
    root: 'workspace-settings-ia-test',
    sceneTabs: 'workspace-settings-ia-test-scenes',
    search: 'workspace-settings-ia-test-search',
  },
  basecampProjectMappingsTest: {
    root: 'basecamp-project-mappings-test',
    sceneTabs: 'basecamp-project-mappings-test-scenes',
    savedNote: 'basecamp-project-mappings-test-saved',
  },
  apiAccess: {
    tab: 'api-access-settings-tab',
    tokenList: 'api-access-token-list',
    newTokenButton: 'api-access-new-token-button',
    generateDialog: 'api-access-generate-dialog',
    tokenNameInput: 'api-access-token-name-input',
    generateButton: 'api-access-generate-button',
    tokenValue: 'api-access-token-value',
    tokenValueToggle: 'api-access-token-value-toggle',
    copyToken: 'api-access-copy-token',
    mcpConfig: 'api-access-mcp-config',
    copyConfig: 'api-access-copy-config',
    doneButton: 'api-access-done-button',
    revokeButton: 'api-access-revoke-button',
    revokeConfirm: 'api-access-revoke-confirm',
  },
  secretsSettings: {
    tab: 'secrets-settings-tab',
    list: 'secrets-list',
    addButton: 'secrets-add-button',
    addDialog: 'secrets-add-dialog',
    nameInput: 'secrets-name-input',
    nameSuggestions: 'secrets-name-suggestions',
    valueInput: 'secrets-value-input',
    valueToggle: 'secrets-value-toggle',
    scopeToggle: 'secrets-scope-toggle',
    saveButton: 'secrets-save-button',
    deleteConfirm: 'secrets-delete-confirm',
    secretRow: (id: string) => `secret-row-${id}`,
  },
  workspaceSecrets: {
    cloudSection: 'workspace-secrets-cloud',
    customSection: 'workspace-secrets-custom',
    extensionSection: 'workspace-secrets-extension',
  },
  gitSourcesSettings: {
    tab: 'git-sources-settings-tab',
    addButton: 'git-sources-add-button',
    list: 'git-sources-list',
    row: (id: string) => `git-source-row-${id}`,
    removeButton: (id: string) => `git-source-remove-${id}`,
    addForm: 'git-sources-add-form',
    repoUrlInput: 'git-sources-repo-url-input',
    fnPathPrefixInput: 'git-sources-fn-path-prefix-input',
    branchInput: 'git-sources-branch-input',
    subpathInput: 'git-sources-subpath-input',
    workingBranchPatternInput: 'git-sources-working-branch-pattern-input',
    providerSelect: 'git-sources-provider-select',
    patInput: 'git-sources-pat-input',
    patToggle: 'git-sources-pat-toggle',
    cancelButton: 'git-sources-cancel-button',
    submitButton: 'git-sources-submit-button',
    removeConfirm: 'git-sources-remove-confirm',
  },
  oauth: {
    basecampDone: 'oauth-basecamp-done',
    googleDriveDone: 'oauth-google-drive-done',
    mcpOAuthBridge: 'mcp-oauth-bridge',
    mcpOAuthSuccess: 'mcp-oauth-success',
    connectCursorBridge: 'connect-cursor-bridge',
  },
  basecamp: {
    myStuffSidebar: 'basecamp-my-stuff-sidebar',
    myStuffToggle: 'basecamp-my-stuff-toggle',
    myStuffRefresh: 'basecamp-my-stuff-refresh',
    activityFeed: 'basecamp-activity-feed',
    activityProjectFilter: 'basecamp-activity-project-filter',
    activityProjectFilterSearch: 'basecamp-activity-project-filter-search',
    activitySearch: 'basecamp-activity-search',
    panePlaceholder: 'basecamp-pane-placeholder',
    activityPreviewToggle: 'basecamp-activity-preview-toggle',
    activitySecondary: 'basecamp-activity-secondary',
    activityPreviewHeader: 'basecamp-activity-preview-header',
    todosSecondary: 'basecamp-todos-secondary',
    todosPreviewHeader: 'basecamp-todos-preview-header',
    projectsSecondary: 'basecamp-projects-secondary',
    projectsPreviewHeader: 'basecamp-projects-preview-header',
    bookmarksSecondary: 'basecamp-bookmarks-secondary',
    bookmarksPreviewHeader: 'basecamp-bookmarks-preview-header',
    bookmarksViewMode: 'basecamp-bookmarks-view-mode',
    todosViewMode: 'basecamp-todos-view-mode',
    projectsViewMode: 'basecamp-projects-view-mode',
    activityRow: (id: string) => `basecamp-activity-row-${id}`,
    activityRowContextMenu: 'basecamp-activity-row-context-menu',
    assignmentCard: (id: string) => `basecamp-assignment-${id}`,
    todoViewer: 'basecamp-todo-viewer',
    todoListViewer: 'basecamp-todo-list-viewer',
    todoListViewerHeader: 'basecamp-todo-list-viewer-header',
    todoListViewerPreviewToggle: 'basecamp-todo-list-viewer-preview-toggle',
    todoListViewerSecondary: 'basecamp-todo-list-viewer-secondary',
    todoListViewerRow: (id: string) => `basecamp-todo-list-viewer-row-${id}`,
    todoEmptyNotes: 'basecamp-todo-empty-notes',
    notesDraftPanel: 'basecamp-notes-draft-panel',
    todoHeader: 'basecamp-todo-header',
    todoIngestButton: 'basecamp-todo-ingest-button',
    /** Actions widget still uses this for Update Work Spec. */
    todoUpdateButton: 'basecamp-todo-update-button',
    todoAddToCurrentChat: 'basecamp-todo-add-to-current-chat',
    todoAssessWorkSpec: 'basecamp-todo-assess-work-spec',
    todoUpdateNotes: 'basecamp-todo-update-notes',
    todoDraftMessage: 'basecamp-todo-draft-message',
    todoFindContext: 'basecamp-todo-find-context',
    todoBookmarkButton: 'basecamp-todo-bookmark-button',
    // Todo viewer layout
    todoViewerScrollRegion: 'basecamp-todo-viewer-scroll',
    todoViewerGrid: 'basecamp-todo-viewer-grid',
    todoViewerDetails: 'basecamp-todo-viewer-details',
    todoViewerWidgets: 'basecamp-todo-viewer-widgets',
    todoViewerWidget: (name: string) => `basecamp-todo-widget-${name}`,
    // Edit pane
    todoEditPane: 'basecamp-todo-edit-pane',
    todoEditAssignees: 'basecamp-todo-edit-assignees',
    todoEditWhenDone: 'basecamp-todo-edit-when-done',
    todoEditDue: 'basecamp-todo-edit-due',
    todoEditDescription: 'basecamp-todo-edit-description',
    todoEditGenerateNotes: 'basecamp-todo-edit-generate-notes',
    todoEditFrontmatter: 'basecamp-todo-edit-frontmatter',
    todoEditAddField: 'basecamp-todo-edit-add-field',
    todoEditNoteTags: 'basecamp-todo-edit-note-tags',
    todoEditNoteTagsSuggest: 'basecamp-todo-edit-note-tags-suggest',
    todoEditSave: 'basecamp-todo-edit-save',
    todoEditCancel: 'basecamp-todo-edit-cancel',
    dueDateEditor: 'basecamp-due-date-editor',
    todosPreviewEmpty: 'basecamp-todos-preview-empty',
    projectsPreviewEmpty: 'basecamp-projects-preview-empty',
    bookmarksPreviewEmpty: 'basecamp-bookmarks-preview-empty',
    activityPreviewEmpty: 'basecamp-activity-preview-empty',
    projectHomePreviewEmpty: 'basecamp-project-home-preview-empty',
    todoListPreviewEmpty: 'basecamp-todo-list-preview-empty',
    todoSubtasksSection: 'basecamp-todo-subtasks-section',
    todoSubtasksList: 'basecamp-todo-subtasks-list',
    todoSubtaskRow: (id: string) => `basecamp-todo-subtask-${id}`,
    todoSubtaskCheckbox: (id: string) => `basecamp-todo-subtask-checkbox-${id}`,
    todosPanel: 'basecamp-todos-panel',
    todosPreviewPane: 'basecamp-todos-preview-pane',
    todosPreviewOpen: 'basecamp-todos-preview-open',
    todosPreviewBreadcrumb: 'basecamp-todos-preview-breadcrumb',
    todosPreviewMeta: 'basecamp-todos-preview-meta',
    todosPreviewFooter: 'basecamp-todos-preview-footer',
    todosPreviewRefresh: 'basecamp-todos-preview-refresh',
    todosPreviewOpenBasecamp: 'basecamp-todos-preview-open-basecamp',
    todosPreviewToggle: 'basecamp-todos-preview-toggle',
    todosAssigneeFacet: 'basecamp-todos-assignee-facet',
    todosListMode: 'basecamp-todos-list-mode',
    todosSearch: 'basecamp-todos-search',
    todosFilters: 'basecamp-todos-filters',
    todosCoverage: 'basecamp-todos-coverage',
    todosShowAll: 'basecamp-todos-show-all',
    todosShowMore: 'basecamp-todos-show-more',
    todosStatusBar: 'basecamp-todos-status-bar',
    todosRow: (id: string) => `basecamp-todos-row-${id}`,
    todosRowContextMenu: 'basecamp-todos-row-context-menu',
    bookmarksPanel: 'basecamp-bookmarks-panel',
    bookmarksProjectFilter: 'basecamp-bookmarks-project-filter',
    bookmarksPreviewToggle: 'basecamp-bookmarks-preview-toggle',
    bookmarksRow: (id: string) => `basecamp-bookmarks-row-${id}`,
    bookmarksRowContextMenu: 'basecamp-bookmarks-row-context-menu',
    /** Home dashboard "Basecamp Bookmarks" card → View All */
    homeBookmarksViewAll: 'home-basecamp-bookmarks-view-all',
    projectsPanel: 'basecamp-projects-panel',
    projectsSearch: 'basecamp-projects-search',
    projectsPreview: 'basecamp-projects-preview',
    projectHome: 'basecamp-project-home',
    projectHomeBreadcrumb: 'basecamp-project-home-breadcrumb',
    projectHomePreviewToggle: 'basecamp-project-home-preview-toggle',
    projectHomeSecondary: 'basecamp-project-home-secondary',
    projectHomeListRow: (id: string) => `basecamp-project-home-list-${id}`,
    projectsPreviewToggle: 'basecamp-projects-preview-toggle',
    projectsDepthFilter: 'basecamp-projects-depth-filter',
    projectsDepthFilterOption: (id: string) =>
      `basecamp-projects-depth-filter-${id}`,
    projectsTitleInfo: 'basecamp-projects-title-info',
    projectsViewOverflow: 'basecamp-projects-view-overflow',
    projectsReloadList: 'basecamp-projects-reload-list',
    projectsRefreshMembership: 'basecamp-projects-refresh-membership',
    projectsRefreshVisible: 'basecamp-projects-refresh-visible',
    projectsSort: 'basecamp-projects-sort',
    projectsSortOption: (id: string) => `basecamp-projects-sort-${id}`,
    projectsStatusBar: 'basecamp-projects-status-bar',
    projectsStatusText: 'basecamp-projects-status-text',
    projectsStatusInfo: 'basecamp-projects-status-info',
    projectsRow: (id: string) => `basecamp-projects-row-${id}`,
    projectsRowBookmarks: (id: string) => `basecamp-projects-row-bookmarks-${id}`,
    projectsRowRefresh: (id: string) => `basecamp-projects-row-refresh-${id}`,
    projectsRowContextMenu: 'basecamp-projects-row-context-menu',
    todoToolbarRefresh: 'basecamp-todo-toolbar-refresh',
    todoCommentReply: 'basecamp-todo-comment-reply',
    todoCommentInput: 'basecamp-todo-comment-input',
    todoCommentSubmit: 'basecamp-todo-comment-submit',
    todoCommentCancel: 'basecamp-todo-comment-cancel',
    todoCommentCopyDraft: 'basecamp-todo-comment-copy-draft',
    todoCommentOpenBasecamp: 'basecamp-todo-comment-open-basecamp',
    /** Cursor workspace trigger - standalone dropdown button or, when embedded in the document
     * viewer's overflow menu (asSubmenu), the "Cursor" submenu trigger row. */
    cursorWorkspaceDropdownTrigger: 'basecamp-cursor-workspace-dropdown-trigger',
    cursorWorkspaceOptionsDialog: 'basecamp-cursor-workspace-options-dialog',
    cursorWorkspaceGitPathInput: 'basecamp-cursor-workspace-git-path-input',
  },
  flowsManager: {
    container: 'flows-manager',
    filterInput: 'flows-manager-filter-input',
    incidentQueueToggle: 'flows-manager-incident-queue-toggle',
    flowRow: (id: string) => `flow-row-${id}`,
    flowTitle: (id: string) => `flow-title-${id}`,
    flowEnableToggle: (id: string) => `flow-enable-toggle-${id}`,
    flowRunButton: (id: string) => `flow-run-button-${id}`,
    flowExpandToggle: (id: string) => `flow-expand-toggle-${id}`,
    flowRunHistory: (id: string) => `flow-run-history-${id}`,
    runRow: (runId: string) => `flow-run-row-${runId}`,
  },
  /** Flow document enriched renderer (`kind=flow` in markdown viewer) */
  flowDocumentRenderer: {
    root: 'flow-document-renderer',
    tabs: 'flow-document-renderer-tabs',
    stepLog: 'flow-document-renderer-step-log',
    enableSwitch: 'flow-document-renderer-enable-switch',
    posture: 'flow-document-renderer-posture',
    refine: 'flow-document-renderer-refine',
    viewMode: 'flow-document-renderer-view-mode',
    runNow: 'flow-document-renderer-run-now',
    basecampAuthBanner: 'flow-document-renderer-basecamp-auth-banner',
    connectBasecamp: 'flow-document-renderer-connect-basecamp',
    builderOverview: 'flow-document-renderer-builder-overview',
    notifyRecipients: 'flow-builder-notify-recipients',
    notifyRecipient: (email: string) => `flow-builder-notify-recipient-${email}`,
    notifySave: 'flow-builder-notify-save',
    deliverBadge: 'flow-document-renderer-deliver-badge',
    scheduleNext: 'flow-document-renderer-schedule-next',
    runSummary: 'flow-document-run-summary',
    runStep: (index: number) => `flow-document-run-step-${index}`,
    definitionPipeline: 'flow-document-definition-pipeline',
    definitionStep: (index: number) => `flow-document-definition-step-${index}`,
    definitionField: (index: number, field: string) =>
      `flow-document-definition-field-${index}-${field}`,
    definitionApprove: 'flow-document-definition-approve',
  },
  flowStudioIndex: {
    root: 'flow-studio-index',
    nav: 'flow-studio-nav',
    tree: 'flow-studio-tree',
    pulse: 'flow-studio-pulse',
    pulseScope: 'flow-studio-pulse-scope',
    pulseFilter: 'flow-studio-pulse-filter',
    pulseRunMenu: 'flow-studio-pulse-run-menu',
    pulseRunDetail: 'flow-studio-pulse-run-detail',
  },
  /** Quiz document renderer (`kind=quiz` in markdown viewer) */
  quizDocumentRenderer: {
    root: 'quiz-document-renderer',
    activeMode: 'quiz-document-renderer-active-mode',
    previewMode: 'quiz-document-renderer-preview-mode',
    resultsMode: 'quiz-document-renderer-results-mode',
    runsTab: 'quiz-document-renderer-runs-tab',
    quizTab: 'quiz-document-renderer-quiz-tab',
    moreMenu: 'quiz-document-renderer-more-menu',
    reviewQuestionsMenuItem: 'quiz-document-renderer-review-questions',
    runRow: (runId: string) => `quiz-document-renderer-run-row-${runId}`,
    reviewMode: 'quiz-document-renderer-review-mode',
    reviewWrongOnly: 'quiz-document-renderer-review-wrong-only',
    reviewPrev: 'quiz-document-renderer-review-prev',
    reviewNext: 'quiz-document-renderer-review-next',
    startOver: 'quiz-document-renderer-start-over',
    practiceTab: 'quiz-document-renderer-practice-tab',
    practiceMode: 'quiz-document-renderer-practice-mode',
    submitButton: 'quiz-document-renderer-submit',
    optionButton: (questionId: string, key: string) =>
      `quiz-document-renderer-option-${questionId}-${key}`,
    /** Play tab after a run is submitted (locked until Play again) */
    completedPlay: 'quiz-document-renderer-completed-play',
    playAgain: 'quiz-document-renderer-play-again',
    viewSummary: 'quiz-document-renderer-view-summary',
    startGate: 'quiz-document-renderer-start-gate',
    startQuiz: 'quiz-document-renderer-start-quiz',
    resumeQuiz: 'quiz-document-renderer-resume-quiz',
    sessionBuilder: 'quiz-document-renderer-session-builder',
    sessionModePractice: 'quiz-document-renderer-session-mode-practice',
    sessionModeTimed: 'quiz-document-renderer-session-mode-timed',
    sessionCount: 'quiz-document-renderer-session-count',
    sessionDomain: (domain: string) =>
      `quiz-document-renderer-session-domain-${domain.replace(/[^a-z0-9_-]/gi, '-')}`,
    sessionVerifiedOnly: 'quiz-document-renderer-session-verified-only',
    sessionExcludeMock: 'quiz-document-renderer-session-exclude-mock',
    sessionHeader: 'quiz-document-renderer-session-header',
    sessionScope: 'quiz-document-renderer-session-scope',
    exitSession: 'quiz-document-renderer-exit-session',
    exitExamConfirm: 'quiz-document-renderer-exit-exam-confirm',
    exitPracticeConfirm: 'quiz-document-renderer-exit-practice-confirm',
  },
  /** Shared sidebar tree panel (Drive, Browse adapters) */
  sidebarTree: {
    panel: (prefix: string) => `${prefix}-tree-panel`,
    row: (prefix: string, id: string) => `${prefix}-tree-row-${id}`,
    breadcrumb: (prefix: string, id: string) => `${prefix}-tree-crumb-${id}`,
    sectionHeader: (prefix: string, id: string) => `${prefix}-tree-section-${id}`,
  },
  /** Publish Maater doc → new Google Doc folder picker */
  drivePublishPicker: {
    root: 'drive-publish-picker',
    corpusNav: 'drive-publish-picker-corpus-nav',
    folderList: 'drive-publish-picker-folder-list',
    folderItem: (id: string) => `drive-publish-picker-folder-${id}`,
    filterInput: 'drive-publish-picker-filter',
    publishButton: 'drive-publish-picker-publish',
    newFolderButton: 'drive-publish-picker-new-folder',
    newFolderForm: 'drive-publish-picker-new-folder-form',
    newFolderInput: 'drive-publish-picker-new-folder-input',
    accountRow: 'drive-publish-picker-account',
    accountSelect: 'drive-publish-picker-account-select',
    settingsLink: 'drive-publish-picker-settings',
  },
  /** Drive sidebar panel (left sidebar icon bar integration) */
  driveSidebar: {
    container: 'drive-sidebar',
    tabBar: 'drive-sidebar-tab-bar',
    tab: (id: string) => `drive-sidebar-tab-${id}`,
    notConnected: 'drive-sidebar-not-connected',
    requestAccess: 'drive-sidebar-request-access',
    needAccessLink: 'drive-sidebar-need-access',
    retryConnect: 'drive-sidebar-retry-connect',
    driveUrlInput: 'drive-sidebar-drive-url-input',
    driveUrlResolve: 'drive-sidebar-drive-url-resolve',
    driveSearch: 'drive-sidebar-drive-search',
    driveList: 'drive-sidebar-drive-list',
    driveItem: (id: string) => `drive-sidebar-drive-item-${id}`,
    driveEmpty: 'drive-sidebar-drive-empty',
    drivePreview: 'drive-sidebar-drive-preview',
    drivePreviewCommentsToggle: 'drive-sidebar-drive-preview-comments-toggle',
    drivePreviewCommentsPanel: 'drive-sidebar-drive-preview-comments-panel',
    drivePreviewCommentItem: (id: string) => `drive-sidebar-drive-preview-comment-${id}`,
    /** "More options" (⋮) menu in the Drive raw-file preview header - Phase 2 toolbar-density cleanup */
    drivePreviewMenuButton: 'drive-sidebar-drive-preview-menu-button',
    drivePreviewOpenInDriveMenuItem: 'drive-sidebar-drive-preview-open-in-drive-menu-item',
    drivePreviewImportButton: 'gdrive-preview-import-button',
    drivePreviewConnectButton: 'gdrive-preview-connect-button',
    driveImportedBadge: (id: string) => `drive-sidebar-imported-badge-${id}`,
    commentsSearch: 'drive-sidebar-comments-search',
    commentsList: 'drive-sidebar-comments-list',
    commentItem: (id: string) => `drive-sidebar-comment-item-${id}`,
    commentsEmpty: 'drive-sidebar-comments-empty',
    linkedSearch: 'drive-sidebar-linked-search',
    linkedList: 'drive-sidebar-linked-list',
    linkedItem: (id: string) => `drive-sidebar-linked-item-${id}`,
    linkedEmpty: 'drive-sidebar-linked-empty',
    importPickerTrigger: 'drive-sidebar-import-picker-trigger',
    importPicker: 'drive-sidebar-import-picker',
    importDestination: 'drive-sidebar-import-destination',
    importDestinationChange: 'drive-sidebar-import-destination-change',
    importProgressPanel: 'drive-sidebar-import-progress-panel',
    importProgressRow: (fileId: string) => `drive-sidebar-import-progress-row-${fileId}`,
    importProgressDismiss: 'drive-sidebar-import-progress-dismiss',
    importFromDriveMenuItem: 'drive-sidebar-import-from-drive-menu-item',
    folderTree: 'drive-sidebar-folder-tree',
    upOneLevel: 'drive-sidebar-up-one-level',
    surfaceNav: 'drive-sidebar-surface-nav',
    workspaceSelect: 'drive-sidebar-workspace-select',
  },
  /** Google Drive main-panel folder view */
  driveFolderView: {
    root: 'drive-folder-view',
    statusBar: 'drive-folder-view-status-bar',
    statusBarHelp: 'drive-folder-view-status-bar-help',
    filters: 'drive-folder-view-filters',
    viewMode: 'drive-folder-view-view-mode',
    secondary: 'drive-folder-view-secondary',
    previewPane: 'drive-folder-view-preview-pane',
    previewHeader: 'drive-folder-view-preview-header',
    previewOpen: 'drive-folder-view-preview-open',
    previewFolderList: 'drive-folder-view-preview-folder-list',
    previewNativeText: 'drive-folder-view-preview-native-text',
    previewImage: 'drive-folder-view-preview-image',
    previewToggle: 'drive-folder-view-preview-toggle',
    folderBack: 'drive-folder-view-back',
    folderForward: 'drive-folder-view-forward',
    folderUp: 'drive-folder-view-up',
    folderUpRow: 'drive-folder-view-up-row',
    previewInfoDetails: 'drive-folder-view-preview-info-details',
    previewInfoHistory: 'drive-folder-view-preview-info-history',
    activityRoot: 'drive-folder-view-activity',
    activityList: 'drive-folder-view-activity-list',
    activityFilter: 'drive-folder-view-activity-filter',
    activityFilterChip: (id: string) => `drive-folder-view-activity-filter-${id}`,
    activityReconnect: 'drive-folder-view-activity-reconnect',
  },
  /** Basecamp icon-rail panel (ADR-0019) */
  basecampSidebar: {
    container: 'basecamp-sidebar',
    workspaceBlock: (workspaceId: string) => `basecamp-sidebar-ws-${workspaceId}`,
    connect: (workspaceId: string) => `basecamp-sidebar-connect-${workspaceId}`,
    optionsSettings: 'basecamp-sidebar-options-settings',
    optionsConnect: 'basecamp-sidebar-options-connect',
    railAlert: 'basecamp-sidebar-rail-alert',
    headerAlert: 'basecamp-sidebar-header-alert',
    paneSettings: 'basecamp-pane-settings',
    section: (workspaceId: string, sectionId: string) =>
      `basecamp-sidebar-ws-${workspaceId}-section-${sectionId}`,
  },
  deskLanding: {
    root: 'desk-landing',
    newNote: 'desk-landing-new-note',
    openPrivate: 'desk-landing-open-private',
  },
  basecampProjectsCache: {
    root: 'basecamp-projects-cache-proto',
    appHeader: 'basecamp-projects-cache-app-header',
    backToIndex: 'basecamp-projects-cache-back-index',
    iconRail: 'basecamp-projects-cache-icon-rail',
    modePanel: 'basecamp-projects-cache-mode-panel',
    tabRow: 'basecamp-projects-cache-tab-row',
    titleRow: 'basecamp-projects-cache-title-row',
    titleInfo: 'basecamp-projects-cache-title-info',
    viewOverflow: 'basecamp-projects-cache-view-overflow',
    refreshVisible: 'basecamp-projects-cache-refresh-visible',
    reloadList: 'basecamp-projects-cache-reload-list',
    previewToggle: 'basecamp-projects-cache-preview-toggle',
    previewPane: 'basecamp-projects-cache-preview-pane',
    previewRefresh: 'basecamp-projects-cache-preview-refresh',
    table: 'basecamp-projects-cache-table',
    statusBar: 'basecamp-projects-cache-status-bar',
    statusText: 'basecamp-projects-cache-status-text',
    statusInfo: 'basecamp-projects-cache-status-info',
    row: (id: string) => `basecamp-projects-cache-row-${id}`,
    rowRefresh: (id: string) => `basecamp-projects-cache-row-refresh-${id}`,
    rowContextMenu: 'basecamp-projects-cache-row-context-menu',
  },
  /** Gold-standard UI gallery (`?test=maater-ui`) - agent import SoT */
  maaterUiGallery: {
    root: 'maater-ui-gallery',
    appHeader: 'maater-ui-gallery-app-header',
    iconRail: 'maater-ui-gallery-icon-rail',
    modePanel: 'maater-ui-gallery-mode-panel',
    sceneNav: 'maater-ui-gallery-scene-nav',
    sceneButton: (id: string) => `maater-ui-gallery-scene-${id}`,
    tabRow: 'maater-ui-gallery-tab-row',
    titleRow: 'maater-ui-gallery-title-row',
    sceneBody: 'maater-ui-gallery-scene-body',
    sceneStatusBanner: 'maater-ui-gallery-scene-status-banner',
    previewToggle: 'maater-ui-gallery-preview-toggle',
    previewPane: 'maater-ui-gallery-preview-pane',
    openDestinations: 'maater-ui-gallery-open-destinations',
    openDestinationRow: (id: string) => `maater-ui-gallery-open-dest-${id}`,
    newTabIconPicker: 'maater-ui-gallery-new-tab-icon-picker',
    newTabIconOption: (id: string) => `maater-ui-gallery-new-tab-icon-${id}`,
    chatIconPicker: 'maater-ui-gallery-chat-icon-picker',
    chatIconOption: (id: string) => `maater-ui-gallery-chat-icon-${id}`,
    listGestures: 'maater-ui-gallery-list-gestures',
    listGesturesUp: 'maater-ui-gallery-list-gestures-up',
    listGesturesStatusBar: 'maater-ui-gallery-list-gestures-status-bar',
    listGesturesHelp: 'maater-ui-gallery-list-gestures-help',
    sidebarTree: 'maater-ui-gallery-sidebar-tree',
    sidebarTreePanel: 'maater-ui-gallery-sidebar-tree-panel',
    sidebarTreeRow: (id: string) => `maater-ui-gallery-sidebar-tree-row-${id}`,
    sidebarTreeMain: 'maater-ui-gallery-sidebar-tree-main',
    sidebarTreeMainRow: (id: string) => `maater-ui-gallery-sidebar-tree-main-row-${id}`,
    sidebarTreePreview: 'maater-ui-gallery-sidebar-tree-preview',
    sidebarTreeNote: 'maater-ui-gallery-sidebar-tree-note',
    sidebarTreeMenu: 'maater-ui-gallery-sidebar-tree-menu',
    sidebarTreeViewMode: 'maater-ui-gallery-sidebar-tree-view-mode',
    sidebarTreeToolbar: 'maater-ui-gallery-sidebar-tree-toolbar',
    sidebarTreeBack: 'maater-ui-gallery-sidebar-tree-back',
    sidebarTreeForward: 'maater-ui-gallery-sidebar-tree-forward',
    sidebarTreeFile: 'maater-ui-gallery-sidebar-tree-file',
    previewDrill: 'maater-ui-gallery-preview-drill',
    previewDrillPane: 'maater-ui-gallery-preview-drill-pane',
    previewDrillRow: (id: string) => `maater-ui-gallery-preview-drill-row-${id}`,
    entityMenu: 'maater-ui-gallery-entity-menu',
    entityMenuOverflow: 'maater-ui-gallery-entity-menu-overflow',
    entityMenuPopup: 'maater-ui-gallery-entity-menu-popup',
    menuChrome: 'maater-ui-gallery-menu-chrome',
    menuChromePanelTrigger: 'maater-ui-gallery-menu-chrome-panel',
    menuChromeViewerTrigger: 'maater-ui-gallery-menu-chrome-viewer',
    menuChromeCompactTrigger: 'maater-ui-gallery-menu-chrome-compact',
    menuChromeDateTrigger: 'maater-ui-gallery-menu-chrome-date',
    menuChromeDestructiveHover: 'maater-ui-gallery-menu-chrome-destructive-hover',
    menuChromeConfirm: 'maater-ui-gallery-menu-chrome-confirm',
    readerChrome: 'maater-ui-gallery-reader-chrome',
    indexVsEntity: 'maater-ui-gallery-index-vs-entity',
    waitSync: 'maater-ui-gallery-wait-sync',
    syncStrip: 'maater-ui-gallery-sync-strip',
    listStatusChrome: 'maater-ui-gallery-list-status-chrome',
    extensionLimb: 'maater-ui-gallery-extension-limb',
    shellScene: 'maater-ui-gallery-shell',
    harnessScene: 'maater-ui-gallery-harness',
    harnessSecondarySlot: 'maater-ui-gallery-harness-secondary-slot',
    harnessToc: 'maater-ui-gallery-harness-toc',
    panelCollapseScene: 'maater-ui-gallery-panel-collapse',
    panelCollapseFrame: 'maater-ui-gallery-panel-collapse-frame',
    panelCollapseFrameWidth: 'maater-ui-gallery-panel-collapse-frame-width',
    panelCollapseAutoHide: 'maater-ui-gallery-panel-collapse-auto-hide',
    panelCollapseMenu: 'maater-ui-gallery-panel-collapse-menu',
    panelCollapseScope: 'maater-ui-gallery-panel-collapse-scope',
    panelCollapseLeftHandle: 'maater-ui-gallery-panel-collapse-left-handle',
    panelCollapseAgentHandle: 'maater-ui-gallery-panel-collapse-agent-handle',
    panelCollapseFlyout: 'maater-ui-gallery-panel-collapse-flyout',
    panelCollapseCanvas: 'maater-ui-gallery-panel-collapse-canvas',
    panelCollapsePreview: 'maater-ui-gallery-panel-collapse-preview',
    panelCollapsePreviewHandle: 'maater-ui-gallery-panel-collapse-preview-handle',
    panelCollapsePreviewToggle: 'maater-ui-gallery-panel-collapse-preview-toggle',
    panelCollapseToolbar: 'maater-ui-gallery-panel-collapse-toolbar',
    panelCollapsePeekHeader: 'maater-ui-gallery-panel-collapse-peek-header',
    panelCollapsePeekActions: 'maater-ui-gallery-panel-collapse-peek-actions',
    panelCollapseDocToolbar: 'maater-ui-gallery-panel-collapse-doc-toolbar',
    panelCollapseDocPaneToggle: 'maater-ui-gallery-panel-collapse-doc-pane-toggle',
    panelCollapseIndexMore: 'maater-ui-gallery-panel-collapse-index-more',
    panelCollapseOpenTabs: 'maater-ui-gallery-panel-collapse-open-tabs',
    sidebarTreePeekHeader: 'maater-ui-gallery-sidebar-tree-peek-header',
    sidebarTreePeekActions: 'maater-ui-gallery-sidebar-tree-peek-actions',
    panelMotionScene: 'maater-ui-gallery-panel-motion',
    panelMotionStylePicker: 'maater-ui-gallery-panel-motion-styles',
    panelMotionStyle: (id: string) => `maater-ui-gallery-panel-motion-style-${id}`,
    panelMotionDemo: 'maater-ui-gallery-panel-motion-demo',
    panelMotionToggle: 'maater-ui-gallery-panel-motion-toggle',
    panelMotionDock: 'maater-ui-gallery-panel-motion-dock',
    tabGroupsScene: 'maater-ui-gallery-tab-groups',
    tabGroupsBar: 'maater-ui-gallery-tab-groups-bar',
    tabGroupsCollapse: (initials: string) => `maater-ui-gallery-tab-groups-collapse-${initials}`,
    tabGroupsGroup: (id: string) => `maater-ui-gallery-tab-groups-group-${id}`,
    tabGroupsOnlyPersonal: 'maater-ui-gallery-tab-groups-only-personal',
    tabGroupsOnlyHop: 'maater-ui-gallery-tab-groups-only-hop',
    tabGroupsRestoreCollapsed: 'maater-ui-gallery-tab-groups-restore-collapsed',
    openEchoScene: 'maater-ui-gallery-open-echo',
    openEchoTree: 'maater-ui-gallery-open-echo-tree',
    openEchoOpenTabs: 'maater-ui-gallery-open-echo-open-tabs',
    settingsChrome: 'maater-ui-gallery-settings-chrome',
    listOverflow: 'maater-ui-gallery-list-overflow',
    listOverflowShowMore: 'maater-ui-gallery-list-overflow-show-more',
    createInFolder: 'maater-ui-gallery-create-in-folder',
    harnessInfoDock: 'maater-ui-gallery-harness-info',
    modePanelStack: 'maater-ui-gallery-mode-panel-stack',
    stackNav: 'maater-ui-gallery-stack-nav',
    prototypeIndex: 'maater-ui-gallery-prototype-index',
    presenceScene: 'maater-ui-gallery-presence',
    presenceCluster: 'maater-ui-gallery-presence-cluster',
    presenceDocMock: 'maater-ui-gallery-presence-doc',
    presenceMemberFilter: 'maater-ui-gallery-presence-member-filter',
    presencePlacement: (id: string) => `maater-ui-gallery-presence-placement-${id}`,
    personView: 'maater-ui-gallery-person-view',
    personViewPreviewToggle: 'maater-ui-gallery-person-view-preview-toggle',
    indexChrome: 'maater-ui-gallery-index-chrome',
    toastChrome: 'maater-ui-gallery-toast-chrome',
    composerOffers: 'maater-ui-gallery-composer-offers',
    composerOffersUseSkill: 'maater-ui-gallery-composer-offers-use-skill',
    composerOffersRemoveSkill: 'maater-ui-gallery-composer-offers-remove-skill',
    inboxAttention: 'maater-ui-gallery-inbox-attention',
    inboxAttentionBack: 'maater-ui-gallery-inbox-attention-back',
    inboxAttentionForward: 'maater-ui-gallery-inbox-attention-forward',
    inboxAttentionFolder: (id: string) => `maater-ui-gallery-inbox-attention-folder-${id}`,
    inboxAttentionView: (id: string) => `maater-ui-gallery-inbox-attention-view-${id}`,
    inboxAttentionRow: (id: string) => `maater-ui-gallery-inbox-attention-row-${id}`,
    inboxAttentionReader: 'maater-ui-gallery-inbox-attention-reader',
    inboxAttentionRecipients: 'maater-ui-gallery-inbox-attention-recipients',
    inboxAttentionAddPeople: 'maater-ui-gallery-inbox-attention-add-people',
    inboxAttentionReply: 'maater-ui-gallery-inbox-attention-reply',
    inboxAttentionSend: 'maater-ui-gallery-inbox-attention-send',
    inboxAttentionOpenChat: 'maater-ui-gallery-inbox-attention-open-chat',
    inboxAttentionCorrect: (id: string) => `maater-ui-gallery-inbox-attention-correct-${id}`,
    diffReview: 'maater-ui-gallery-diff-review',
    diffReviewMode: (id: string) => `maater-ui-gallery-diff-review-mode-${id}`,
    diffReviewTitle: 'maater-ui-gallery-diff-review-title',
    diffReviewReader: 'maater-ui-gallery-diff-review-reader',
    diffReviewHunk: (id: string) => `maater-ui-gallery-diff-review-hunk-${id}`,
    diffReviewHunkMark: (id: string, kind: 'add' | 'remove') =>
      `maater-ui-gallery-diff-review-hunk-${id}-mark-${kind}`,
    diffReviewHunkGutter: (id: string, kind: 'add' | 'remove') =>
      `maater-ui-gallery-diff-review-hunk-${id}-gutter-${kind}`,
    diffReviewHunkActions: (id: string, kind: 'add' | 'remove') =>
      `maater-ui-gallery-diff-review-hunk-${id}-actions-${kind}`,
    diffReviewLand: 'maater-ui-gallery-diff-review-land',
    diffReviewMiss: 'maater-ui-gallery-diff-review-miss',
    diffReviewAccept: 'maater-ui-gallery-diff-review-accept',
    diffReviewReject: 'maater-ui-gallery-diff-review-reject',
    diffReviewEdit: 'maater-ui-gallery-diff-review-edit',
    diffReviewHunkAccept: (id: string) => `maater-ui-gallery-diff-review-hunk-${id}-accept`,
    diffReviewHunkReject: (id: string) => `maater-ui-gallery-diff-review-hunk-${id}-reject`,
    diffReviewHunkEdit: (id: string) => `maater-ui-gallery-diff-review-hunk-${id}-edit`,
    diffReviewFilePrev: 'maater-ui-gallery-diff-review-file-prev',
    diffReviewFileNext: 'maater-ui-gallery-diff-review-file-next',
    diffReviewStatus: 'maater-ui-gallery-diff-review-status',
    diffReviewHelp: 'maater-ui-gallery-diff-review-help',
    diffReviewCompare: 'maater-ui-gallery-diff-review-compare',
    diffReviewCompareView: (id: string) => `maater-ui-gallery-diff-review-compare-${id}`,
    toastChromeFireUndo: 'maater-ui-gallery-toast-chrome-fire-undo',
  },
  personView: {
    root: 'person-view',
    list: 'person-view-list',
    preview: 'person-view-preview',
    previewHeader: 'person-view-preview-header',
    previewToggle: 'person-view-preview-toggle',
    backButton: 'person-view-back-button',
    forwardButton: 'person-view-forward-button',
    statusBar: 'person-view-status-bar',
    statusBarHelp: 'person-view-status-bar-help',
    segment: 'person-view-segment',
    allEdits: 'person-view-all-edits',
    workspaceOverflow: 'person-view-workspace-overflow',
    scopeSelector: 'person-view-scope-selector',
  },
  peopleIndex: {
    root: 'people-index',
    header: 'people-index-header',
    backButton: 'people-index-back-button',
    forwardButton: 'people-index-forward-button',
    list: 'people-index-list',
    preview: 'people-index-preview',
    previewHeader: 'people-index-preview-header',
    previewIdentity: 'people-index-preview-identity',
    previewEmail: 'people-index-preview-email',
    previewActivity: 'people-index-preview-activity',
    previewAssignment: 'people-index-preview-assignment',
    previewToggle: 'people-index-preview-toggle',
    statusBar: 'people-index-status-bar',
    statusBarHelp: 'people-index-status-bar-help',
    scopeSelector: 'people-index-scope-selector',
  },
  /** In-doc comments + suggestions prototype (`?test=doc-comments`) */
  docCommentsPrototype: {
    root: 'doc-comments-prototype',
    appHeader: 'doc-comments-app-header',
    iconRail: 'doc-comments-icon-rail',
    titleRow: 'doc-comments-title-row',
    prototypeIndex: 'doc-comments-prototype-index',
    docPane: 'doc-comments-doc-pane',
    commentsRail: 'doc-comments-rail',
    railToggle: 'doc-comments-rail-toggle',
    railFilter: 'doc-comments-rail-filter',
    railScope: 'doc-comments-rail-scope',
    railContext: 'doc-comments-rail-context',
    railGroup: (blockId: string) => `doc-comments-rail-group-${blockId}`,
    railComposer: 'doc-comments-rail-composer',
    railComposerInput: 'doc-comments-rail-composer-input',
    railComposerMode: 'doc-comments-rail-composer-mode',
    railComposerSubmit: 'doc-comments-rail-composer-submit',
    mentionButton: 'doc-comments-mention-button',
    mentionMenu: 'doc-comments-mention-menu',
    mentionChips: 'doc-comments-mention-chips',
    openInChat: 'doc-comments-open-in-chat',
    agentPanel: 'doc-comments-agent-panel',
    /** @deprecated use commentsRail - kept for any stray refs */
    sidebar: 'doc-comments-rail',
    sidebarToggle: 'doc-comments-rail-toggle',
    switchDialog: 'doc-comments-switch-dialog',
    orchLog: 'doc-comments-orch-log',
    orchReview: 'doc-comments-orch-review',
    block: (id: string) => `doc-comments-block-${id}`,
    gutterBadge: (id: string) => `doc-comments-badge-${id}`,
    addComment: (id: string) => `doc-comments-add-${id}`,
    tableCell: (blockId: string, row: number, col: number) =>
      `doc-comments-cell-${blockId}-${row}-${col}`,
    listItem: (blockId: string, index: number) =>
      `doc-comments-li-${blockId}-${index}`,
    railCard: (id: string) => `doc-comments-rail-card-${id}`,
    suggestionCard: (id: string) => `doc-comments-rail-card-${id}`,
    acceptSuggestion: (id: string) => `doc-comments-accept-${id}`,
  },
  /** Google Drive dev test page (`?test=google-drive-extension`) */
  googleDriveTest: {
    root: 'google-drive-test',
    viewToggle: 'google-drive-test-view-toggle',
    appHeader: 'google-drive-test-app-header',
    iconRail: 'google-drive-test-icon-rail',
    modePanel: 'google-drive-test-mode-panel',
    surfaceNav: 'google-drive-test-surface-nav',
    tabRow: 'google-drive-test-tab-row',
    titleRow: 'google-drive-test-title-row',
    backButton: 'google-drive-test-back',
    forwardButton: 'google-drive-test-forward',
    mainList: 'google-drive-test-main-list',
    previewPane: 'google-drive-test-preview-pane',
    previewToggle: 'google-drive-test-preview-toggle',
    syncStrip: 'google-drive-test-sync-strip',
    syncButton: 'google-drive-test-sync',
    keepSyncedToggle: 'google-drive-test-keep-synced',
    contextMenu: 'google-drive-test-context-menu',
    backToPrototypeIndex: 'google-drive-test-back-prototype-index',
    authStatus: 'google-drive-test-auth-status',
    connectButton: 'google-drive-test-connect',
    disconnectButton: 'google-drive-test-disconnect',
    folderBack: 'google-drive-test-folder-back',
    folderForward: 'google-drive-test-folder-forward',
    folderUp: 'google-drive-test-folder-up',
    statusBar: 'google-drive-test-status-bar',
    statusBarHelp: 'google-drive-test-status-bar-help',
    authMock: 'google-drive-test-auth-mock',
    statusInfo: 'google-drive-test-status-info',
    previewResizeHandle: 'google-drive-test-preview-resize',
    driveSection: 'google-drive-test-drive-section',
    modePanelMenu: 'google-drive-test-mode-panel-menu',
    viewModeStrip: 'google-drive-test-view-mode-strip',
    viewModeToggle: 'google-drive-test-view-mode',
    importFileIdInput: 'google-drive-test-import-fileid-input',
    importTitleInput: 'google-drive-test-import-title-input',
    importSubmit: 'google-drive-test-import-submit',
    importResult: 'google-drive-test-import-result',
    commentsFileIdInput: 'google-drive-test-comments-fileid-input',
    commentsSubmit: 'google-drive-test-comments-submit',
    commentsResult: 'google-drive-test-comments-result',
    pushDocIdInput: 'google-drive-test-push-docid-input',
    pushSubmit: 'google-drive-test-push-submit',
    pushResult: 'google-drive-test-push-result',
  },
  /** Asana extension UI prototype (`?test=asana-extension`) */
  asanaExtensionTest: {
    root: 'asana-extension-test',
    sceneTabs: 'asana-extension-test-scene-tabs',
    settingsPanel: 'asana-extension-test-settings',
    connectButton: 'asana-extension-test-connect',
    browseSidebar: 'asana-extension-test-browse',
    asanaNavRow: 'asana-extension-test-asana-nav',
    addProjectButton: 'asana-extension-test-add-project',
    addProjectDialog: 'asana-extension-test-add-project-dialog',
    mainPane: 'asana-extension-test-main',
    taskViewer: 'asana-extension-test-task-viewer',
    taskRow: (taskGid: string) => `asana-extension-test-task-${taskGid}`,
    createSpecOpen: 'asana-extension-test-create-spec-open',
    createSpecDialog: 'asana-extension-test-create-spec-dialog',
    createSpecConfirm: 'asana-extension-test-create-spec-confirm',
    docBadge: 'asana-extension-test-doc-badge',
  },
  /** Basecamp extension chrome prototype (`?test=basecamp-extension`) */
  basecampExtensionTest: {
    root: 'basecamp-extension-test',
    sceneTabs: 'basecamp-extension-test-scene-tabs',
    settingsPanel: 'basecamp-extension-test-settings',
    connectButton: 'basecamp-extension-test-connect',
    listeningPanel: 'basecamp-extension-test-listening',
    browseSidebar: 'basecamp-extension-test-browse',
    basecampNavRow: 'basecamp-extension-test-basecamp-nav',
    mainPane: 'basecamp-extension-test-main',
    bookmarksPanel: 'basecamp-extension-test-bookmarks',
    todosPanel: 'basecamp-extension-test-todos',
    todosPreview: 'basecamp-extension-test-todos-preview',
    todosPreviewToggle: 'basecamp-extension-test-todos-preview-toggle',
    bookmarksPreviewToggle: 'basecamp-extension-test-bookmarks-preview-toggle',
    projectsPreview: 'basecamp-extension-test-projects-preview',
    projectsPreviewToggle: 'basecamp-extension-test-projects-preview-toggle',
    todolistPreviewToggle: 'basecamp-extension-test-todolist-preview-toggle',
    activityPreviewToggle: 'basecamp-extension-test-activity-preview-toggle',
    projectsPanel: 'basecamp-extension-test-projects',
    projectPanel: 'basecamp-extension-test-project',
    projectPreviewToggle: 'basecamp-extension-test-project-preview-toggle',
    projectSecondaryPanel: 'basecamp-extension-test-project-secondary',
    projectsFocusPanel: 'basecamp-extension-test-projects-focus',
    projectsFocusShowHidden: 'basecamp-extension-test-projects-focus-show-hidden',
    projectsFocusRowMenu: (id: string) =>
      `basecamp-extension-test-projects-focus-menu-${id}`,
    activityPanel: 'basecamp-extension-test-activity',
    todolistPanel: 'basecamp-extension-test-todolist',
    todoViewer: 'basecamp-extension-test-todo-viewer',
    todoEmptyNotes: 'basecamp-extension-test-todo-empty-notes',
    todoSecondaryPanel: 'basecamp-extension-test-todo-secondary',
    todoPanelToggle: 'basecamp-extension-test-todo-panel-toggle',
    todoBookmarkToggle: 'basecamp-extension-test-todo-bookmark',
    navBack: 'basecamp-extension-test-nav-back',
    navForward: 'basecamp-extension-test-nav-forward',
    projectsBreadcrumb: 'basecamp-extension-test-projects-breadcrumb',
    todoRow: (todoId: string) => `basecamp-extension-test-todo-${todoId}`,
    createSpecOpen: 'basecamp-extension-test-create-spec-open',
    createSpecDialog: 'basecamp-extension-test-create-spec-dialog',
    createSpecConfirm: 'basecamp-extension-test-create-spec-confirm',
    docBadge: 'basecamp-extension-test-doc-badge',
  },
  diffOverview: {
    scrollPane: 'diff-overview-scroll-pane',
    ruler: 'diff-overview-ruler',
  },
  diffPreviewDialog: {
    /** Split/Unified segmented-control toggle, styled to match documentContentStatusBar.viewModeTabs. */
    viewModeTabs: 'diff-preview-dialog-view-mode-tabs',
    restoreButton: 'diff-preview-dialog-restore-button',
    cancelButton: 'diff-preview-dialog-cancel-button',
    keepLocalButton: 'diff-preview-dialog-keep-local-button',
    acceptRemoteButton: 'diff-preview-dialog-accept-remote-button',
  },
  /** Basecamp Todos chrome proto (`?test=basecamp-todos`) */
  basecampTodosTest: {
    root: 'basecamp-todos-test',
    appHeader: 'basecamp-todos-test-app-header',
    iconRail: 'basecamp-todos-test-icon-rail',
    modePanel: 'basecamp-todos-test-mode-panel',
    tabRow: 'basecamp-todos-test-tab-row',
    tabTodos: 'basecamp-todos-test-tab-todos',
    chromeStrip: 'basecamp-todos-test-chrome-strip',
    previewToggle: 'basecamp-todos-test-preview-toggle',
    previewPane: 'basecamp-todos-test-preview-pane',
    previewBreadcrumb: 'basecamp-todos-test-preview-breadcrumb',
    previewMeta: 'basecamp-todos-test-preview-meta',
    previewFooter: 'basecamp-todos-test-preview-footer',
    previewRefresh: 'basecamp-todos-test-preview-refresh',
    previewOpenBasecamp: 'basecamp-todos-test-preview-open-basecamp',
    statusBar: 'basecamp-todos-test-status-bar',
    contextMenu: 'basecamp-todos-test-context-menu',
    backToPrototypeIndex: 'basecamp-todos-test-back-prototype-index',
  },
  /** Browse My documents + extension icon rail (`?test=browse-desk-sidebar`) */
  browseDeskSidebarTest: {
    root: 'browse-desk-sidebar-test',
    iconRail: 'browse-desk-sidebar-test-icon-rail',
    railBrowse: 'browse-desk-sidebar-test-rail-browse',
    railDrive: 'browse-desk-sidebar-test-rail-drive',
    railBasecamp: 'browse-desk-sidebar-test-rail-basecamp',
    browsePanel: 'browse-desk-sidebar-test-browse-panel',
    basecampPanel: 'browse-desk-sidebar-test-basecamp-panel',
    mainPane: 'browse-desk-sidebar-test-main',
    deskLanding: 'browse-desk-sidebar-test-desk-landing',
    myDocumentsRow: 'browse-desk-sidebar-test-my-documents',
    documentsRow: 'browse-desk-sidebar-test-documents',
    workspaceToggle: (id: string) => `browse-desk-sidebar-test-ws-${id}`,
  },
  /** Nextcloud extension prototype (`?test=nextcloud-extension`) */
  nextcloud: {
    harness: 'nextcloud-harness',
    settingsShell: 'nextcloud-settings-shell',
    instanceUrlInput: 'nextcloud-instance-url-input',
    usernameInput: 'nextcloud-username-input',
    passwordInput: 'nextcloud-password-input',
    defaultRootInput: 'nextcloud-default-root-input',
    connectButton: 'nextcloud-connect-button',
    disconnectButton: 'nextcloud-disconnect-button',
    importFolderInput: 'nextcloud-import-folder-input',
    testConnectionButton: 'nextcloud-test-connection-button',
    sidebarPanel: 'nextcloud-sidebar-panel',
    fileTree: 'nextcloud-file-tree',
    browseView: 'nextcloud-browse-view',
    treeRow: 'nextcloud-tree-row',
    menuButton: 'nextcloud-menu-button',
    previewToggle: 'nextcloud-preview-toggle',
    previewPane: 'nextcloud-preview-pane',
    previewHeader: 'nextcloud-preview-header',
    fileRow: 'nextcloud-file-row',
    folderToolbar: 'nextcloud-folder-toolbar',
    filterRow: 'nextcloud-filter-row',
    viewMode: 'nextcloud-view-mode',
    documentCrumbs: 'nextcloud-document-crumbs',
    documentInfo: 'nextcloud-document-info',
    optionsMenu: 'nextcloud-options-menu',
    settingsMenuItem: 'nextcloud-settings-menu-item',
    accountSettings: 'nextcloud-account-settings',
    accountSelect: 'nextcloud-account-select',
  },
  /** Web queue edit prototype (`?test=chat-queue-edit`) */
  chatQueueEditPrototype: {
    root: 'chat-queue-edit-prototype',
    strip: 'chat-queue-edit-strip',
    item: (id: string) => `chat-queue-edit-item-${id}`,
    edit: (id: string) => `chat-queue-edit-edit-${id}`,
    banner: 'chat-queue-edit-banner',
    exit: 'chat-queue-edit-exit',
    sendNow: (id: string) => `chat-queue-edit-send-now-${id}`,
    cancel: (id: string) => `chat-queue-edit-cancel-${id}`,
    composer: 'chat-queue-edit-composer',
    streamEnded: 'chat-queue-edit-stream-ended',
    stop: 'chat-queue-edit-stop',
    log: 'chat-queue-edit-log',
  },
  /** In-document chat edit review (single pending edit). */
  documentEditReview: {
    root: 'document-edit-review',
    title: 'document-edit-review-title',
    reader: 'document-edit-review-reader',
    status: 'document-edit-review-status',
    acceptAll: 'document-edit-review-accept-all',
    rejectAll: 'document-edit-review-reject-all',
    close: 'document-edit-review-close',
    prev: 'document-edit-review-prev',
    next: 'document-edit-review-next',
    hunk: (id: string) => `document-edit-review-hunk-${id}`,
    hunkMark: (id: string, kind: 'add' | 'remove') => `document-edit-review-hunk-${id}-mark-${kind}`,
    hunkGutter: (id: string, kind: 'add' | 'remove') => `document-edit-review-hunk-${id}-gutter-${kind}`,
    hunkActions: (id: string, kind: 'add' | 'remove') => `document-edit-review-hunk-${id}-actions-${kind}`,
    hunkAccept: (id: string) => `document-edit-review-hunk-${id}-accept`,
    hunkReject: (id: string) => `document-edit-review-hunk-${id}-reject`,
  },
  /** In-note component kits (`?test=structured-doc-blocks`) */
  structuredDocBlocks: {
    root: 'structured-doc-blocks',
    specimen: 'structured-doc-blocks-specimen',
    importReview: 'structured-doc-blocks-import-review',
    preview: 'structured-doc-blocks-preview',
    source: 'structured-doc-blocks-source',
    article: 'structured-doc-blocks-article',
    filters: 'structured-doc-blocks-filters',
    filter: (area: string) => `structured-doc-blocks-filter-${area}`,
    unimported: 'structured-doc-blocks-unimported',
  },
} as const

